/**
 * Timeline <-> OPBridge wiring (2026-08-16 — closes the OPBridge S218 ask:
 * `opbridge-timelines` shipped server-side and is live on beta, but
 * `TimelineLauncher.tsx` never called it — every press only ran the local
 * visual-playhead simulation. See OPApps/opbridge-opbuttons.md "RESPONSE
 * 2026-08-16 (OPBridge S218)".
 *
 * Two responsibilities, same shape as every other engine's dispatch file
 * (`opbridge/dispatch.ts`'s sequencer/macro sections):
 *
 *  1. **Wire translation** (`toBridgeTimeline`) — resolve every
 *     `buttonRef` step into the underlying layout item's actual MIDI
 *     action(s) AT PUSH TIME, same "resolve fresh, don't freeze at author
 *     time" contract `dispatch.ts::toBridgeSequencer` already uses for
 *     port injection. A `buttonRef` is a reference (id + optional
 *     duration); OPBridge's engine has no concept of layout items at all,
 *     so the PWA is the only place this resolution can happen.
 *
 *  2. **Dispatch** (`push`/`fire`/`start`/`stop`/`pause`/`resume`/
 *     `jumpToSection`/`readState`) — same target-resolution + never-throws
 *     contract as every `dispatch.ts` function. Kept in its own file
 *     rather than folded into dispatch.ts because the wire translation is
 *     large and Timeline-specific.
 *
 * ## Server support ceiling (byte-verified against OPBridge's own source,
 * not guessed — `crates/opbridge-timelines/src/lib.rs` +
 * `crates/opbridge-core/src/handlers/timelines.rs`, 2026-08-16)
 *
 *  - [CORRECTED 2026-10-19: `animation` steps (Light FX blocks) now GO to a
 *    bridge that says `/health.capabilities.timeline_lighting` (OPBridge
 *    `v1.15.167`, their S295 ask), as `{kind:"animation", positionBars,
 *    durationBars, preset, params?}` with `preset` one of that bridge's
 *    `GET /lighting/presets` ids. Our operator's go, 2026-10-19 ("yes"), ended
 *    the hold that DEC-249's design work had kept since 2026-10-02.]
 *  - [2026-10-02, OPBridge S300: OPBridge plays our LIGHTS LANES -
 *    `colorMarker` keyframes and `lightRelease` Hand backs, ported from our own
 *    `colorSample.ts` + `lightMix.ts` and checked against them number for
 *    number. A bridge that says `/health.capabilities.timeline_colour: true`
 *    gets them as they are (see `bridgePlaysColour`); an older one still has
 *    them dropped here, because it would refuse the whole push. A silent
 *    lights lane goes with `mute: true` so the rig can turn that light OFF.]
 *  - Only `cue` steps exist server-side (M2 + core of M3). `animation` /
 *    `colorMarker` / `marker` / `targetValue` / `sequencer` / `macro`
 *    ref-steps have NO runtime yet (M4/M6/M7) — dropped client-side with a
 *    console warning, never sent, rather than risk a 422 or a silently
 *    ignored field.
 *  - `Timeline.tier` is a closed Rust enum of exactly `Action` | `Song` —
 *    **`genre` does not exist server-side yet** (M5). A genre-tier
 *    timeline is refused at push time (see `toBridgeTimeline`), same
 *    "unsupported, not silently wrong" posture as a missing MIDI port.
 *  - Every `midi`/`sendRaw` Cue action REQUIRES an explicit `port` in
 *    `args` — confirmed against `rules_wiring.rs::fire_module_action`,
 *    which silently DROPS the action ("midi action missing port;
 *    dropping") when `port` is absent. There is no bridge-side
 *    `defaultMidiOutput` fallback for a Cue the way there is for a plain
 *    `/midi/send` HTTP call, so every resolved action here always carries
 *    a concrete port string, resolved from the item/pedal or the target
 *    bridge's own `defaultMidiOutput`.
 *  - Same-bar, same-lane Cue steps fire in AUTHORED ARRAY ORDER, not
 *    arbitrary order — confirmed against `PlayheadRuntime::new`'s
 *    `sort_by(|a,b| ...total_cmp...)`, which is a stable sort (Rust's
 *    `sort_by` guarantee), so ties keep their original array position.
 *    This is what makes a 3-message PresetChange compound send (setlist
 *    CC, then Program Change, then optional snapshot CC) land in the
 *    right order when expanded into 3 same-bar Cue steps below.
 *
 * ## Where a timeline plays (2026-09-29)
 *
 * A timeline plays on ONE bridge, its "home": the machine its device-bound
 * steps drive (a Helix preset is bound to the pedal's machine; a button with
 * an explicit "Sends to" is bound to those machines), and the primary when no
 * step is bound to a machine. See {@link timelinePlacement}.
 *
 * It used to be "whichever bridge is primary on the device that launched it".
 * Measured on the operator's rig that day: the Helix lives on the Mac Studio,
 * the desktop browser's primary is the Gaming-PC, so every Helix preset step
 * compiled against a machine with no Helix port and never played, with no
 * warning, while the same item tapped on the canvas worked (a tap is aimed at
 * the pedal's own bridge). Plan: `.notepads/next-update-plan.md` item 1.2.
 *
 * Nothing is dropped silently any more. Every step that cannot play from the
 * bridge being pushed to is named in `droppedWarning` with its reason, and
 * every MIDI cue is checked against that bridge's live port list. The port
 * check WARNS and never removes a cue: on macOS OPBridge's own ports are
 * CoreMIDI virtual sources that `GET /midi/ports` does not list as outputs,
 * so a remove-on-miss rule would have silenced every Ableton note on the Mac.
 */

import { opvCellLabel, opvControlDef, type OpvControlDef } from './opvControls.ts';
import { getConnection, resolveBridges, resolveBridge, primaryBridge, bridges } from '../opbridge/store.ts';
import { shapeAt, autoValueAtExact } from './automation.ts';
import { crossfadeSend, landSends, motionBusesOf, motionGlideOut, motionLandsAt, motionMessages, motionOscArgs, motionTarget, MOTION_OFF_ADDRESS, MOTION_OSC_TARGET, MOTION_SEG_ADDRESS, OPBRIDGE_BEATS_PER_BAR, SHAPE_INT } from './opMotion.ts';
import { autoPickOpVisualBridge } from '../opvisual/machine.ts';
import { chainLane, chainSteps, isChain, isWaitStep, waitProblem } from './chain.ts';
import type { BridgeConfig } from '../opbridge/storage.ts';
import { OpbridgeError, type RuleAction, type TimelineStateSnapshot, type OpbridgeHttpClient, type TimelineQuantize, type JumpToBarAnswer } from '../opbridge/httpClient.ts';
import type { FireResult } from '../opbridge/dispatch.ts';
import { plainFireReason } from '../opbridge/dispatch.ts';
import { leaseHeaderFor, noteLeaseRefused } from './lease.ts';
import { activeProject, activePedals, motionPresetById, projects } from '../layout/projectStore.ts';
import type {
  ButtonItem, PresetChangeItem, SnapshotChangeItem, StreamDeckItem, LayoutItem,
} from '../layout/projectStore.ts';
import {
  isCueStep, isButtonRefStep, isColorMarkerStep, isLightReleaseStep, isPatternStep, unrollPatterns, patternOf, timelineLoops, timelineLeadIn, timelinePlaySections, leadInSectionId, timelineOrigin, timelineStartsDark, timelineEndsDark,
  isHitRouteStep, isHitBlockStep, isHitStep, unrollHitBlocks, hitSourceKey, hitBlockLookId, hitBlockWireLook, timelineTotalBars, HIT_SILENCE_LOOK,
  type HitRouteStep, type InstrumentLook,
  normalizeRecQuant, normalizeLaunchQuant,
  unknownFieldsOf,
  TIMELINE_KNOWN_KEYS, TIMELINE_SECTION_KNOWN_KEYS,
  TIMELINE_LANE_KNOWN_KEYS, TIMELINE_CUE_STEP_KNOWN_KEYS,
  type Timeline, type TimelineLane, type TimelineStep, type AbletonStep, type AutomationPointStep,
  type ColorMarkerStep, type LightReleaseStep, type CueStep, type TimelineSection,
} from '@opbuttons/schema';
import {
  ABLETON_SYNC_TARGET,
  ABLETON_TEMPO_ADDRESS, ABLETON_TIME_SIG_ADDRESS,
  recQuantSends, ABLETON_LAUNCH_QUANT_ADDRESS,
  ABLETON_METRONOME_ADDRESS,
  ABLETON_END_SESSION_RECORD_ADDRESS,
  ABLETON_RESYNC_ADDRESS,
  ABLETON_SYNC_RECEIVE_TARGET,
  ABLETON_SUSTAIN_KILL_ADDRESS, SUSTAIN_STOP_TARGET,
  ABLETON_PLAY_ADDRESS, ABLETON_STOP_ADDRESS, ABLETON_CONTINUE_ADDRESS,
} from '../opbridge/dispatch.ts';
import { pedalBridgeRowId, UNRESOLVED_BRIDGE } from '../pedals/bridgeBinding.ts';
import { fixtures } from '../fixtures/store.ts';
import { darkStartSteps, lightColourWith, lightGroupOf, mastersReaching, OFF, ownColourOf, takeoverColour, takeoversOf, takeoverWeight } from './lightMix.ts';
import { blockLookLights, cleanWireLook, sourceLabel } from './drumLights.ts';
import { hueSceneAction } from '../lighting/hueScenes.ts';
import { beatmaps } from './testTrackStore.ts';
import { effectiveClips } from './testTrack.ts';
import { transportRun } from './transport.ts';

const CONSUMER = 'opbuttons';

// ============================================================
// buttonRef resolution — PaletteFireableItem -> RuleAction[]
// ============================================================
//
// Mirrors TimelineEditor.tsx's own `PaletteFireableItem` allowlist
// (ButtonItem | PresetChangeItem | SnapshotChangeItem | StreamDeckItem) —
// the ONLY kinds a buttonRef step can reference, by construction of the
// palette drag source. StreamDeck is in that allowlist for AUTHORING
// (DEC-153/172 widened the palette) but confirmed NOT reachable for
// FIRING (OPBridge S218 response: it rides a separate HTTP proxy,
// `/streamdeck/actions`, not the module/command dispatch a Cue goes
// through) — so it resolves to `null` here, same as a removed item.

export type FireableItem = ButtonItem | PresetChangeItem | SnapshotChangeItem | StreamDeckItem;

function isFireableItem(item: LayoutItem): item is FireableItem {
  return item.kind === 'button' || item.kind === 'presetChange'
    || item.kind === 'snapshotChange' || item.kind === 'streamdeck';
}

/** The project a timeline compile reads items and pedals from (2026-10-16): the timeline's OWN (`projectId`, DEC-235),
 *  not whichever project is on screen. Before, a Launcher on one project's page pointing at another project's timeline
 *  compiled it against the wrong project: every button step dropped, and a timeline left with only lights overwrote
 *  OPBridge's full copy with a gutted one (the reliability audit). Null outside a compile, so a live press keeps
 *  reading the active project. Set by {@link inProjectOf} at each compile entry point. */
let compileProject: { pages: Array<{ items: LayoutItem[] }>; pedals?: ReturnType<typeof activePedals> | undefined } | null = null;

function inProjectOf<T>(t: Timeline, fn: () => T): T {
  const prev = compileProject;
  compileProject = (t.projectId ? projects().find((p) => p.id === t.projectId) : undefined) ?? null;
  try { return fn(); } finally { compileProject = prev; }
}

/** The pedals in scope: the compiled timeline's project's, else the active project's. */
function scopedPedals(): ReturnType<typeof activePedals> {
  return compileProject ? (compileProject.pedals ?? []) : activePedals();
}

/** A layout item by id: in the compiled timeline's project (or the active project outside a compile), then in every
 *  project, for a timeline older than projects owning timelines. Mirrors `projectStore.ts::allMidiTriggerItems`'s own
 *  "search every page" reach. */
function findLayoutItemById(id: string): LayoutItem | undefined {
  const first = compileProject ?? activeProject();
  const look = (proj: { pages: Array<{ items: LayoutItem[] }> } | null | undefined) => {
    for (const pg of proj?.pages ?? []) {
      const found = pg.items.find((i) => i.id === id);
      if (found) return found;
    }
    return undefined;
  };
  return look(first) ?? projects().map(look).find((x) => x !== undefined);
}

const clampByte = (n: number): number => Math.max(0, Math.min(127, Math.round(n))) & 0x7f;
const clampNibble = (n: number): number => Math.max(0, Math.min(15, Math.round(n))) & 0x0f;

/** An item's own bridge-affinity target strings — mirrors each live
 *  component's own resolution exactly (FireButton.tsx's `targets()`,
 *  PresetChange.tsx/SnapshotChange.tsx's `pedal()`), so a Timeline push
 *  agrees with what a manual tap on the same tile would do. Pedal-bound
 *  items (PresetChange/SnapshotChange always; Button when `pedalId` is
 *  set) inherit the pedal's single bridge; everything else uses the
 *  item's own `bridgeIds` (undefined = default bridge, `fanoutTargets`'s
 *  `['']` convention). */
function itemBridgeTargets(item: ButtonItem | PresetChangeItem | SnapshotChangeItem): string[] {
  const pedalId = item.pedalId;
  if (pedalId) {
    const pedal = scopedPedals().find((pd) => pd.id === pedalId);
    return pedal ? [pedalBridgeRowId(pedal) ?? ''] : [];
  }
  const bridgeIds = 'bridgeIds' in item ? item.bridgeIds : undefined;
  return bridgeIds === undefined ? [''] : bridgeIds;
}

function targetsIncludeBridge(targets: string[], cfg: BridgeConfig): boolean {
  // ignorePanicLatch: this is a pure membership READ ("would this target
  // resolve to that bridge") and sends nothing, so the latch must not make
  // it answer false and silently change what the UI believes is wired up.
  return targets.some((t) => resolveBridges(t, { ignorePanicLatch: true }).some((b) => b.id === cfg.id));
}

// ============================================================
// Placement - which bridge a timeline plays on (2026-09-29)
// ============================================================

/** Where one step's item fires, as far as a timeline is concerned.
 *  - `follows`: not tied to a machine (no pedal, no explicit "Sends to", or
 *    "all"), so it plays wherever the timeline plays.
 *  - `bound`: tied to these machines (the pedal's bridge, or explicit targets).
 *  - `unresolved`: tied to a machine this device has no row for (a pedal on a
 *    computer this browser never paired with), or to a pedal that was removed. */
type ItemPlacement =
  | { kind: 'follows' }
  | { kind: 'bound'; bridges: BridgeConfig[] }
  | { kind: 'unresolved' };

/** Resolved exactly the way a manual tap resolves it (see {@link itemBridgeTargets}),
 *  so a timeline and a tap can never disagree about which machine an item lives on. */
function itemPlacement(item: FireableItem): ItemPlacement {
  if (item.kind === 'streamdeck') return { kind: 'follows' };
  // A Hue scene (2026-10-23) is recalled on the PC holding the Hue bridge from any computer: it pulls the timeline nowhere.
  if (item.kind === 'button' && item.actionType === 'hueScene') return { kind: 'follows' };
  const read = { ignorePanicLatch: true } as const;
  if (item.pedalId) {
    const pedal = scopedPedals().find((pd) => pd.id === item.pedalId);
    if (!pedal) return { kind: 'unresolved' };
    const rowId = pedalBridgeRowId(pedal);
    // A legacy pedal with no bridge at all follows the default, as a tap does.
    if (!rowId) return { kind: 'follows' };
    if (rowId === UNRESOLVED_BRIDGE) return { kind: 'unresolved' };
    const rows = resolveBridges(rowId, read);
    return rows.length > 0 ? { kind: 'bound', bridges: rows } : { kind: 'unresolved' };
  }
  const ids = 'bridgeIds' in item ? item.bridgeIds : undefined;
  if (ids === undefined) return { kind: 'follows' };
  const named = ids.filter((id) => id !== '');
  if (named.length === 0 || named.includes('all')) return { kind: 'follows' };
  const rows = new Map<string, BridgeConfig>();
  // `''` inside an explicit list still means the primary, as it does for a tap.
  for (const id of ids) for (const b of resolveBridges(id, read)) rows.set(b.id, b);
  return rows.size > 0 ? { kind: 'bound', bridges: [...rows.values()] } : { kind: 'unresolved' };
}

/** Button action types a timeline cannot play (each is dropped, with a named
 *  warning, in {@link resolveStepToCues}). Kept out of the placement tally so
 *  an OBS button aimed at the streaming PC cannot pull a whole timeline onto a
 *  machine where none of its steps can play. */
const UNPLAYABLE_BUTTON_ACTIONS = new Set(['obs', 'runProgram', 'lightPreset', 'abletonDevice']);

export function canPlayFromTimeline(item: FireableItem): boolean {
  if (item.kind === 'streamdeck') return false;
  if (item.kind === 'button' && UNPLAYABLE_BUTTON_ACTIONS.has(item.actionType ?? '')) return false;
  return true;
}

/**
 * Whether a lane plays (2026-10-02, operator: "mute and solo on a lane makes
 * sense"): not when it is muted, and not when another lane is soloed and it
 * is not. The schema reserved `mute`/`solo` long ago; OPBridge stores them as
 * free extras and its engine ignores them (read at their source, 2026-10-02),
 * so they are honoured HERE, in the compile: a silent lane goes to OPBridge
 * with no steps. The canonical timeline (doc store) keeps every step, and
 * nothing reads the engine copy back. OPBridge keeps a running timeline on the
 * copy it started with, so a mute made mid-play applies from the next play.
 */
export function laneAudible(t: Timeline, lane: TimelineLane): boolean {
  if (lane.mute) return false;
  return !anySolo(t) || !!lane.solo;
}

/** The steps of `lane` that play: none inside the timeline's LEAD-IN (`firstSectionBar`, 2026-10-19: "lights and cues
 *  wait" until the first section), except that a light's last colour before the first section is carried onto its
 *  first bar, so moving the intro's left edge never leaves a light with no colour where the intro starts. */
export function playedSteps(t: Timeline, lane: TimelineLane): TimelineStep[] {
  // A pattern block goes as its keys written out (2026-10-20): OPBridge never sees a pattern. A drum lights block
  // (2026-10-23) goes as its routes written out, ending with a route to nothing where it ends.
  const steps: TimelineStep[] = isInstrumentLane(lane) ? unrollHitBlocks(lane.steps, timelineTotalBars(t)) : unrollPatterns(lane.steps);
  const lead = timelineLeadIn(t);
  if (lead <= 0) return steps;
  const after = steps.filter((st) => st.position >= lead - 1e-9);
  if (isInstrumentLane(lane)) {
    // A route HOLDS until the next one for its source: the last of each source before the first section carries onto
    // it (unless one starts there), so a block that starts in the lead-in still plays from the first section.
    const carried = new Map<string, HitRouteStep>();
    for (const st of steps) if (isHitRouteStep(st) && st.position < lead - 1e-9) carried.set(hitSourceKey(st.source), st);
    const startsThere = new Set(after.filter((st): st is HitRouteStep => isHitRouteStep(st) && Math.abs(st.position - lead) < 1e-9).map((st) => hitSourceKey(st.source)));
    const carry = [...carried.entries()].filter(([k, r]) => !startsThere.has(k) && r.lookId !== HIT_SILENCE_LOOK).map(([, r]) => ({ ...r, position: lead }));
    return [...carry, ...after];
  }
  const before = steps.filter((st) => st.position < lead - 1e-9 && (isColorMarkerStep(st) || isLightReleaseStep(st)));
  const last = before.reduce<TimelineStep | null>((m, st) => (!m || st.position >= m.position ? st : m), null);
  if (!last || !isColorMarkerStep(last) || after.some((st) => Math.abs(st.position - lead) < 1e-9 && isColorMarkerStep(st))) return after;
  return [{ ...last, position: lead }, ...after];
}

/** A solo anywhere: on a lane, or on the song (2026-10-16). */
function anySolo(t: Timeline): boolean {
  return t.lanes.some((l) => l.solo) || !!t.testTrack?.solo;
}

/** Whether the timeline's song plays with it: not muted, and not silenced by a solo elsewhere (2026-10-16, the song
 *  row's mute / solo). False when there is no song. */
export function songAudible(t: Timeline): boolean {
  const s = t.testTrack;
  if (!s || s.mute) return false;
  return !anySolo(t) || !!s.solo;
}

/** An "All lights" (`dmx-rig`) or single-light (`dmx-fixture`) lane. */
function isLightsLane(lane: TimelineLane): boolean {
  return lane.laneKind === 'dmx-rig' || lane.laneKind === 'dmx-fixture';
}

/** A DRUM LIGHTS lane (2026-10-23, `laneKind: 'instrument'`): live hits light the rig through its routes and blocks. */
export function isInstrumentLane(lane: TimelineLane): boolean {
  return lane.laneKind === 'instrument';
}

/** The light ids a drum lane's looks name (`null` = a block's "every light"). For placement: they pull the timeline to
 *  the PC that owns them. */
function instrumentLaneLights(t: Timeline, lane: TimelineLane): Array<string | null> {
  const out: Array<string | null> = [];
  for (const s of lane.steps) {
    if (isHitBlockStep(s)) {
      for (const l of [s.look, ...(s.fill && !s.fill.off ? [s.fill.look] : [])]) {
        if (l.allLights) out.push(null); else out.push(...l.lights);
      }
    } else if (isHitRouteStep(s)) {
      const lk = (t.instrumentLooks ?? []).find((x) => x.id === s.lookId);
      for (const id of lk?.lights ?? []) out.push(id);
    }
  }
  return out;
}

/** The rig a drum look's "every light" means on `cfg`: the lights `cfg` drives when the rig names owners, else all. */
function rigOn(cfg: BridgeConfig): ReturnType<typeof fixtures> {
  const rig = fixtures();
  const node = cfg.nodeId;
  return node && rig.some((f) => f.outputNodeId === node) ? rig.filter((f) => f.outputNodeId === node) : rig;
}

/** The paired bridges that drive a lights lane's lights: the one light's
 *  owner (`outputNodeId`, DEC-471) for a single-light lane, every owner in the
 *  rig for an All-lights lane. Empty when the rig names no owner (offline, or
 *  an older OPBridge), so the lane then pulls nowhere. */
function lightOwnerBridges(lane: TimelineLane): BridgeConfig[] {
  const rig = fixtures();
  const owners = new Set<string>();
  if (lane.laneKind === 'dmx-fixture') {
    const owner = rig.find((f) => f.id === lane.fixtureRef?.fixtureId)?.outputNodeId;
    if (owner) owners.add(owner);
  } else {
    for (const f of rig) if (f.outputNodeId) owners.add(f.outputNodeId);
  }
  return bridges().filter((b) => !!b.nodeId && owners.has(b.nodeId));
}

/** Whether `cfg` plays our lights lanes (OPBridge S300,
 *  `/health.capabilities.timeline_colour`). Read from the last health that
 *  bridge answered; `false` until it has answered one, so a colour step is
 *  never sent to a bridge that would refuse the whole push for it. */
function bridgePlaysColour(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['timeline_colour'] === true;
}
/** OPBridge S329: a lane may drive ONE zone of a light (`fixtureRef.subfixture`, `timeline_light_zones`). A bridge
 *  without it would play a zone lane on the whole light, every zone lane fighting over it, so it gets none. */
function bridgePlaysLightZones(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['timeline_light_zones'] === true;
}
/** OPBridge S329: `w` drives a light's white LEDs and `wTemp` their warm-to-cool (`timeline_white_leds`). */
function bridgePlaysWhites(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['timeline_white_leds'] === true;
}

/** OPBridge S331: the timeline's `lightsAtEnd` is honoured when it stops (`timeline_lights_at_end`). */
export function bridgeEndsLightsDark(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['timeline_lights_at_end'] === true;
}

/** Whether `cfg` plays Light FX blocks (`animation` steps) from a timeline: OPBridge `v1.15.167`
 *  `timeline_lighting`. An older bridge refuses a push holding one, so they are dropped for it and said. */
function bridgePlaysLightFx(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['timeline_lighting'] === true;
}

/** Whether `cfg` routes live hits through a timeline's drum lights lanes (OPBridge S331, `timeline_instrument_routing`,
 *  released v1.15.211). Only such a bridge is sent an `instrument` lane's routes and the timeline's looks. */
/** Whether `cfg` plays a drum lane's DESIGNED hits (`hit` steps) while OP-MidiFeed is absent (OPBridge S339,
 *  `timeline_designed_hits`, released v1.15.222). */
export function bridgePlaysDesignedHits(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['timeline_designed_hits'] === true;
}

export function bridgePlaysInstruments(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['timeline_instrument_routing'] === true;
}

/** Whether `cfg` plays CHAINS (OPBridge S307, `timeline_chains`: steps in order with ms / beat / bar waits). */
export function bridgePlaysChains(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['timeline_chains'] === true;
}

/** Whether `cfg` scopes a master to its light group (DEC-251,
 *  `/health.capabilities.timeline_light_groups`). A bridge that plays colour
 *  but not groups plays every master over EVERY light, so a timeline that
 *  separates lights must not send it a master at all (see the lane map in
 *  `toBridgeTimeline`): a light taken over that the operator separated is
 *  the exact thing groups exist to prevent. */
function bridgePlaysLightGroups(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['timeline_light_groups'] === true;
}

/** Whether `cfg` plays an All-lights master (OPBridge S318, `timeline_all_lights_master`, DEC-329). An older bridge
 *  gets the lane without the flag, so it plays as an ordinary master of its group. */
function bridgePlaysAllLights(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['timeline_all_lights_master'] === true;
}

/** Whether `cfg` lands a launch where it is told to (OPBridge S306,
 *  `/health.capabilities.timeline_quantize`). An older bridge gets no
 *  `quantize` field, so its defaults hold: `start` waits for the bar,
 *  `fire` does not. */
function bridgeQuantizes(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['timeline_quantize'] === true;
}

/** Whether `cfg` honours a section's own end rule - `onEnd: 'loop'` replays the
 *  section until a jump moves on, `'stop'` holds at its end (OPBridge S311,
 *  `/health.capabilities.timeline_section_loop`; DEC-227 "sections loop until
 *  you move on"). An older bridge is sent no `onEnd`, so it plays straight
 *  through as before, and the push names the sections that will not loop there. */
export function bridgePlaysSectionLoop(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['timeline_section_loop'] === true;
}

/** Whether `cfg` starts a song timeline from any bar and jumps a playing one to any bar (asked of OPBridge
 *  2026-10-13, `/health.capabilities.timeline_start_at_bar`). Without it a start is from the top and a jump is to a
 *  section's start, and the editor says so instead of drawing a playhead that lies. */
export function bridgeStartsAtBar(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['timeline_start_at_bar'] === true;
}

/** Whether every bridge a timeline's home target resolves to starts and jumps at any bar (the editor's check). */
export function homeStartsAtBar(target = ''): boolean {
  const ts = resolveBridges(target);
  return ts.length > 0 && ts.every(bridgeStartsAtBar);
}

/** OPBridge loops a range of a running song itself (S324, `timeline_loop_range`). */
export function bridgeLoopsRange(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['timeline_loop_range'] === true;
}
export function homeLoopsRange(target = ''): boolean {
  const ts = resolveBridges(target);
  return ts.length > 0 && ts.every(bridgeLoopsRange);
}
/** Set (or with null clear) the running song's loop range on every bridge of `target`; true when all took it. */
export async function setLoopRangeOnBridge(range: { fromBar: number; toBar: number } | null, target = ''): Promise<boolean> {
  const targets = resolveBridges(target);
  if (targets.length === 0) return false;
  const results = await Promise.all(targets.map((cfg) => runOnBridge(cfg, (c) => c.loopRangeTimeline(CONSUMER, range))));
  return results.every((r) => r.kind === 'ok');
}

/** OPBridge jumps a running song timeline AT ONCE, keeping its clock's bar grid (S329, `timeline_jump_instant`). */
export function bridgeJumpsInstantly(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['timeline_jump_instant'] === true;
}
/** Whether every bridge of a timeline's home target jumps instantly (the editor's seek then asks for it). */
export function homeJumpsInstantly(target = ''): boolean {
  const ts = resolveBridges(target);
  return ts.length > 0 && ts.every(bridgeJumpsInstantly);
}
/** The editor's seek where the home jumps instantly (operator, 2026-10-19: "instant to closest bar line is good"):
 *  `jumpToBar {bar, quantize: "instant"}` to every bridge of `target`, and the first one's answer, which says where
 *  and on which clock beat the run lands (or `nextBar`, when it could not jump at once). */
export async function jumpToBarInstantAction(bar: number, target = ''): Promise<{ results: FireResult[]; answer: JumpToBarAnswer | null }> {
  const targets = resolveBridges(target);
  if (targets.length === 0) return { results: [{ target: target || '<default>', kind: 'no-bridges' }], answer: null };
  let answer: JumpToBarAnswer | null = null;
  const results = await Promise.all(targets.map((cfg) => runOnBridge(cfg, async (c) => { const a = await c.jumpToBarTimeline(CONSUMER, bar, 'instant'); answer ??= a; })));
  return { results, answer };
}

/** `POST /timelines/opbuttons/jumpToBar`: only to a bridge that does it ({@link bridgeStartsAtBar}). */
export async function jumpToBarAction(bar: number, target = ''): Promise<FireResult[]> {
  const targets = resolveBridges(target);
  if (targets.length === 0) return [{ target: target || '<default>', kind: 'no-bridges' }];
  return Promise.all(targets.map((cfg) => runOnBridge(cfg, (c) => c.jumpToBarTimeline(CONSUMER, bar))));
}

/** Whether `cfg` plays a timeline's linked test track with it (OPBridge S318,
 *  `/health.capabilities.timeline_test_track`): only such a bridge is sent `testTrack`. */
export function bridgeBringsSong(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['timeline_test_track'] === true;
}

/** Whether `cfg` plays a timeline's song CLIPS (proposed to OPBridge 2026-10-13, `timeline_song_clips`): only
 *  such a bridge is sent `testTrack.clips`; any other plays the song by its section marks. */
/** The clips as OPBridge must play them: the three numbers, on the song's current grid when it is loaded. */
function wireClips(tt: NonNullable<Timeline['testTrack']>): Array<{ atBeat: number; fromBeat: number; beats: number }> {
  const m = beatmaps()[tt.id];
  const list = m ? effectiveClips(tt.clips ?? [], tt.clipsGrid, m) : (tt.clips ?? []);
  return list.map((c) => ({ atBeat: c.atBeat, fromBeat: c.fromBeat, beats: c.beats }));
}

export function bridgeSongClips(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['timeline_song_clips'] === true;
}

/** Whether `cfg` can skip a song's intro (asked of OPBridge 2026-10-13, `timeline_song_skip_intro`): only such a
 *  bridge is sent `testTrack.skipIntro`; any other plays the intro as today. */
export function bridgeSkipsIntro(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['timeline_song_skip_intro'] === true;
}

/** Whether `cfg`'s toggle module takes an idempotent `set` (OPBridge S314,
 *  `/health.capabilities.toggle_set`, DEC-269 Phase B). */
export function bridgeSetsToggles(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['toggle_set'] === true;
}

/** Whether `cfg` reports each timeline step it fired, with the port write's
 *  result (OPBridge S314, `/health.capabilities.timeline_fired`, DEC-269
 *  Phase B). Such a bridge gets no `pwa` light cues: the canvas lights from
 *  its frames instead, so a send that failed never lights. */
export function bridgeReportsFired(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['timeline_fired'] === true;
}

/** Whether `cfg` can hand a pushed copy to a RUNNING timeline (OPBridge
 *  S306, `PUT ...?live=true`, `/health.capabilities.timeline_live_update`). */
export function bridgeUpdatesLive(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['timeline_live_update'] === true;
}

/** OPBridge refuses an OLDER timeline over a newer one itself (S324, `PUT` answers 409 `newer_copy`), so a launch need
 *  not read the library first to find out, and the read-then-push race is gone. */
export function bridgeGuardsNewer(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['timeline_put_newer_guard'] === true;
}

/** The stored `modified` from a 409 `newer_copy` refusal, or null when the error is anything else. */
function newerCopyRefusal(err: unknown): string | null {
  if (!(err instanceof OpbridgeError) || err.httpStatus !== 409) return null;
  const at = err.message.indexOf('{');
  if (at < 0) return null;
  try {
    const body = JSON.parse(err.message.slice(at)) as { error?: unknown; modified?: unknown };
    return body.error === 'newer_copy' ? (typeof body.modified === 'string' ? body.modified : '') : null;
  } catch {
    return null;
  }
}

export interface TimelinePlacement {
  /** The bridge this timeline plays on. `null` only when nothing is paired. */
  home: BridgeConfig | null;
  /** `devices`: chosen from the machines its steps drive. `primary`: no step
   *  is tied to a machine, so it plays on the primary, as a tap would. `song`: its song is set to play on that
   *  computer ({@link songHome}). */
  basis: 'devices' | 'primary' | 'song';
}

/**
 * The computer a Song timeline's song is set to play on, when the timeline should START there (2026-10-28, OPBridge
 * S342 shape). Operator: "i need the audio coming out of this pc" - the dev PC - while the lights stay on STREAMPC.
 * OPBridge refuses a song on another PC than the one the timeline starts on (`{"playing":false,"reason":"other_pc"}`),
 * because the song follows its timeline's jumps, so the song's PC is the home: it plays the song, leads the mesh clock
 * from it and carries the start to the others, and the light lanes still go to their owner as its part
 * ({@link timelineParts}).
 *
 * Only while that computer is connected and carries presses and songs: otherwise the home it would be is a single
 * part and STREAMPC's lights would play nowhere, so the timeline stays where its devices are and only the song is lost
 * (said by the launch toast).
 */
function songHome(t: Timeline): BridgeConfig | null {
  const pc = t.testTrack?.pc;
  if (!pc || t.tier !== 'song' || isChain(t) || !songAudible(t)) return null;
  const cfg = bridges().find((b) => b.nodeId === pc);
  return cfg && isUp(cfg) && bridgeCarriesPresses(cfg) && bridgeBringsSong(cfg) ? cfg : null;
}

/**
 * Which bridge a timeline plays on: the machine most of its device-bound
 * steps live on (a tie goes to the primary), else the primary.
 *
 * Steps with no machine of their own follow that choice, which is what makes
 * a timeline holding Helix presets (Mac Studio) and plain notes play BOTH on
 * the Mac, wherever it was launched from. Reactive: reads the active
 * project's items and pedals and the bridge list.
 */
export function timelinePlacement(t: Timeline): TimelinePlacement {
  return inProjectOf(t, () => {
  const song = songHome(t);
  if (song) return { home: song, basis: 'song' };
  // Hue lights are on wi-fi, so any OPBridge that says so drives them (2026-10-28, OPBridge `timeline_hue_any_pc`;
  // operator: "hue lights are wifi so can be any opbridge", driven by "the PC the timeline starts on"). Placed without
  // the Hue lanes first: a home that drives Hue from anywhere keeps them; any other is placed as before, so the Hue
  // lanes still pull it to the PC that paired them.
  const free = placeByLanes(t, true);
  return free.home && bridgeDrivesHueAnywhere(free.home) ? free : placeByLanes(t, false);
  });
}

/** A lane for ONE Hue light (`hue:<bridge>:<light>`, OPBridge's id for a Hue light in the rig). */
function isHueLane(lane: TimelineLane): boolean {
  return lane.laneKind === 'dmx-fixture' && !!lane.fixtureRef?.fixtureId?.startsWith('hue:');
}

/** Whether a "lights start dark" lane for rig light `f` belongs in the copy sent to `cfg` (`isHome`: that copy is
 *  the home's). */
function darkensHere(f: { id: string; outputNodeId?: string | null }, cfg: BridgeConfig, isHome: boolean): boolean {
  if (f.id.startsWith('hue:')) return isHome;
  if (f.outputNodeId && f.outputNodeId === cfg.nodeId) return true;
  if (!isHome) return false;
  if (!f.outputNodeId) return true;
  const owner = bridges().find((b) => b.nodeId === f.outputNodeId);
  return !owner || !isUp(owner);
}

/** OPBridge (2026-10-28): it drives a Hue light paired on another PC when a timeline lane names it. */
function bridgeDrivesHueAnywhere(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['timeline_hue_any_pc'] === true;
}

/** {@link timelinePlacement} from the lanes alone; with `hueFollows`, a Hue light's lane pulls nowhere. */
function placeByLanes(t: Timeline, hueFollows: boolean): TimelinePlacement {
  const tally = new Map<string, { cfg: BridgeConfig; n: number }>();
  // A muted lane plays nowhere, so it does not pull the timeline to its
  // machine (a muted Helix lane must not send the buttons to the Mac).
  // Light colours play on the bridge that drives those lights (OPBridge S300:
  // a lighting layer renders only on the PC whose DMX output owns the light).
  // They choose the machine ONLY when no press is tied to one: a timeline
  // holding Helix presets keeps playing where its presets are, as before, and
  // never moves to the lights' PC and drops them. A mixed timeline's lights
  // then light nothing unless the presets' machine also owns the lights - the
  // open cross-PC question (OPBridge DEC-629), not settled here.
  const lightTally = new Map<string, { cfg: BridgeConfig; n: number }>();
  // OPVisual knob, colour and palette lanes (2026-10-23 / 10-28) play on every connected computer (`laneOwners`); they still pull the
  // timeline there - last, after presses and lights, the same "choose only when nothing else is tied" rule.
  const opvTally = new Map<string, { cfg: BridgeConfig; n: number }>();
  for (const lane of t.lanes.filter((l) => laneAudible(t, l))) {
    // OP-Motion lanes (2026-10-23) reach the device only from the computer running Live, the primary: they pull the
    // timeline there like a press tied to it (`motionCues`).
    if (lane.laneKind === 'automation' && lane.motion && lane.steps.length > 0) {
      const live = primaryBridge();
      if (live) { const seen = tally.get(live.id); if (seen) seen.n += lane.steps.length; else tally.set(live.id, { cfg: live, n: lane.steps.length }); }
      continue;
    }
    if (((lane.laneKind === 'automation' && (lane.vizKnob || lane.opvControl)) || isOpvShowLane(lane)) && lane.steps.length > 0) {
      const opv = opVisualBridge();
      if (opv) { const seen = opvTally.get(opv.id); if (seen) seen.n += lane.steps.length; else opvTally.set(opv.id, { cfg: opv, n: lane.steps.length }); }
      continue;
    }
    // Drum lights (2026-10-23) render on the PC that owns their lights (OPBridge DEC-629), like a lights lane.
    if (isInstrumentLane(lane)) {
      const rig = fixtures();
      for (const id of instrumentLaneLights(t, lane)) {
        const owners = new Set<string>();
        if (id === null) { for (const f of rig) if (f.outputNodeId) owners.add(f.outputNodeId); }
        else { const o = rig.find((f) => f.id === id)?.outputNodeId; if (o) owners.add(o); }
        for (const cfg of bridges().filter((b) => !!b.nodeId && owners.has(b.nodeId))) {
          const seen = lightTally.get(cfg.id);
          if (seen) seen.n += 1; else lightTally.set(cfg.id, { cfg, n: 1 });
        }
      }
      continue;
    }
    const owners = isLightsLane(lane) && !(hueFollows && isHueLane(lane)) ? lightOwnerBridges(lane) : [];
    for (const step of lane.steps) {
      if (isColorMarkerStep(step) || isLightReleaseStep(step) || isPatternStep(step)) {
        for (const cfg of owners) {
          const seen = lightTally.get(cfg.id);
          if (seen) seen.n += 1;
          else lightTally.set(cfg.id, { cfg, n: 1 });
        }
        continue;
      }
      if (!isButtonRefStep(step)) continue;
      const item = findLayoutItemById(step.buttonId);
      if (!item || !isFireableItem(item) || !canPlayFromTimeline(item)) continue;
      const p = itemPlacement(item);
      if (p.kind !== 'bound') continue;
      for (const cfg of p.bridges) {
        const seen = tally.get(cfg.id);
        if (seen) seen.n += 1;
        else tally.set(cfg.id, { cfg, n: 1 });
      }
    }
  }
  const primary = primaryBridge();
  const pick = (from: Map<string, { cfg: BridgeConfig; n: number }>) => {
    let best: { cfg: BridgeConfig; n: number } | null = null;
    for (const entry of from.values()) {
      if (!best || entry.n > best.n || (entry.n === best.n && entry.cfg.id === primary?.id)) best = entry;
    }
    return best;
  };
  const best = pick(tally) ?? pick(lightTally) ?? pick(opvTally);
  return best ? { home: best.cfg, basis: 'devices' } : { home: primary, basis: 'primary' };
}

/** Whether this timeline's home bridge plays lights lanes (OPBridge S300,
 *  `timeline_colour`). The editor's "won't play yet" marks on lights read this
 *  (2026-10-05), so they are gone on a bridge that plays colours and still
 *  shown on one that does not; `false` until the home bridge has answered a
 *  health check, the same rule the compile uses. */
export function timelinePlaysLights(t: Timeline): boolean {
  const home = timelinePlacement(t).home;
  return !!home && bridgePlaysColour(home);
}

/** Whether `t`'s home bridge plays drum lights (`timeline_instrument_routing`): the editor's "won't play yet" reads it. */
export function timelinePlaysInstruments(t: Timeline): boolean {
  const home = timelinePlacement(t).home;
  return !!home && bridgePlaysInstruments(home);
}

/** Whether `t`'s home bridge plays Light FX blocks (`timeline_lighting`). */
export function timelinePlaysLightFx(t: Timeline): boolean {
  const home = timelinePlacement(t).home;
  return !!home && bridgePlaysLightFx(home);
}

/** OPBridge S340: `quantize: "playNow"` (`/health.capabilities.timeline_play_now`). With Live stopped the run starts
 *  at once on its own clock at Live's last tempo and moves onto Live's first bar line when Live plays; with the clock
 *  running it is a Next-bar start. */
export function bridgePlaysNow(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['timeline_play_now'] === true;
}

/** OPBridge S340: a start, jump, stop, pause, resume or loop range sent to ONE computer is carried to every linked
 *  computer holding a timeline with the same id, all landing on one beat (`/health.capabilities.timeline_rig_follows`). */
export function bridgeCarriesPresses(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['timeline_rig_follows'] === true;
}

function isUp(cfg: BridgeConfig | null | undefined): cfg is BridgeConfig {
  return !!cfg && getConnection(cfg.id)?.status() === 'connected';
}

/** One computer's share of a timeline (DEC-314, adapted 2026-10-27 and 2026-10-28: "the whole rig follows"). */
export interface TimelinePart {
  cfg: BridgeConfig;
  /** What that computer is sent: the timeline whole (id, sections, loop, bpm, signature), with only the lanes and steps
   *  it plays. The song (`testTrack`) is the home's alone. */
  def: Timeline;
  /** Steps tied to no computer play here: the home only, so a plain note never sounds from two computers. */
  followsHere: boolean;
  /** The other computers holding a part: a step tied to one of them plays there, so it is skipped here unsaid. */
  siblings: ReadonlySet<string>;
  /** Names of the lanes here only as a PREVIEW of another computer's lights while that computer is off (OPBridge S337:
   *  drawn on this computer, never sent to a light, what OPStage shows as "previewed here"). */
  previews: string[];
}

export interface TimelineParts {
  /** The computer the timeline belongs to ({@link timelinePlacement}): its untied steps and its song play there. */
  home: BridgeConfig | null;
  parts: TimelinePart[];
  /** Computers holding a part that are not connected now: the lanes that therefore play nowhere (`silent`) and the
   *  lights lanes the computers that are on preview instead (`previewed`). */
  offline: Array<{ cfg: BridgeConfig; silent: string[]; previewed: string[] }>;
}

/** The computers a lane plays on by its own nature, or null when its steps decide (a press tied to a pedal's machine,
 *  a plain note that follows the home). Empty = nothing names one, so it plays on the home. */
function laneOwners(t: Timeline, lane: TimelineLane, home: BridgeConfig): BridgeConfig[] | null {
  // A Hue light plays on the home when the home drives Hue from any PC (2026-10-28): empty = "plays on the home".
  if (isHueLane(lane) && bridgeDrivesHueAnywhere(home)) return [];
  // An All-lights master reaches EVERY computer that plays one (DEC-329, operator 2026-10-30: "yes every pc"), and each
  // OPBridge paints only its own lights. Not from the rig: our `GET /fixtures` does not ask for Hue and Key Light rows
  // (`?include=network`, OPBridge DEC-700), so a PC owning only those would read as owning nothing. The DMX owners stay
  // listed, so an off owner is still previewed and an older one still gets the lane as a master of its group.
  if (lane.laneKind === 'dmx-rig' && lane.allLights === true) {
    const owners = lightOwnerBridges(lane);
    return [...owners, ...bridges().filter((b) => isUp(b) && bridgePlaysAllLights(b) && !owners.includes(b))];
  }
  // Light colours and drum lights render on the PC whose DMX output owns the light (OPBridge DEC-629).
  if (isLightsLane(lane)) return lightOwnerBridges(lane);
  if (isInstrumentLane(lane)) {
    const rig = fixtures();
    const owners = new Set<string>();
    for (const id of instrumentLaneLights(t, lane)) {
      if (id === null) { for (const f of rig) if (f.outputNodeId) owners.add(f.outputNodeId); }
      else { const o = rig.find((f) => f.id === id)?.outputNodeId; if (o) owners.add(o); }
    }
    return bridges().filter((b) => !!b.nodeId && owners.has(b.nodeId));
  }
  // OP-Motion reaches the device only from the computer running Live.
  if (lane.laneKind === 'automation' && lane.motion) { const p = primaryBridge(); return p ? [p] : []; }
  // OPVisual's colour, palette and knobs go to EVERY connected computer (2026-10-28, OPVisual's ask; operator: "it
  // should send to all opvisuals"): each OPBridge plays its own copy and hands an `opvisual` cue only to the OPVisual on
  // its own socket, so every OPVisual gets each message once and a PC without one simply has nobody to hand it to.
  // Never a fan-out from one bridge (a PC playing the timeline too would get it twice). An off PC is left out, so it
  // is not reported as a part that plays nowhere.
  if ((lane.laneKind === 'automation' && (lane.vizKnob || lane.opvControl)) || isOpvShowLane(lane)) return bridges().filter(isUp);
  return null;
}

/**
 * Which computer gets which part of a timeline (2026-10-28; OPBridge S340 shape (b), accepted 2026-10-27, and S342).
 *
 * Operator, 2026-10-25: "if i play a song timeline on the mac i'd like for the opbridges on the other mesh computers to
 * auto follow"; 2026-10-28: "the entecc is connected to the streampc and the opstage needs to display the virtual
 * lights as tho the streampc was on". Before, a timeline played on ONE computer (DEC-224): a timeline with the Mac's
 * Helix and STREAMPC's lights lost its lights, and with STREAMPC off a lighting timeline played nowhere, so OPStage
 * stayed dark.
 *
 * Every part carries the same id, so a press on any computer (a pedal included) starts them all on one beat; OPBridge
 * carries it. The home gets the steps tied to no computer, its own steps and the song; a computer a step is tied to gets
 * those steps; a light's owner gets the lanes for its lights; and while an owner is OFF, every computer that is on gets
 * those lights lanes too, as a preview (OPBridge S337 draws them there and never on hardware). A lane muted or soloed
 * still goes where it plays, so a mute pushed live silences it there.
 *
 * Only for a Song timeline on a computer that carries presses (`timeline_rig_follows`); anything else plays on its home
 * alone, as before. Reactive: reads the rig, the pairings and which computers are connected.
 */
export function timelineParts(t: Timeline): TimelineParts {
  return inProjectOf(t, () => {
    const home = timelinePlacement(t).home;
    if (!home) return { home: null, parts: [], offline: [] };
    const single: TimelineParts = { home, parts: [{ cfg: home, def: t, followsHere: true, siblings: new Set(), previews: [] }], offline: [] };
    if (t.tier !== 'song' || isChain(t)) return single;
    const carrier = isUp(home) ? home : bridges().find(isUp);
    if (!carrier || !bridgeCarriesPresses(carrier)) return single;

    const shares = new Map<string, { cfg: BridgeConfig; lanes: TimelineLane[]; previews: string[] }>();
    const share = (cfg: BridgeConfig) => {
      let s = shares.get(cfg.id);
      if (!s) { s = { cfg, lanes: [], previews: [] }; shares.set(cfg.id, s); }
      return s;
    };
    share(home);
    const up = bridges().filter(isUp);
    for (const lane of t.lanes) {
      const owners = laneOwners(t, lane, home);
      if (owners === null) {
        share(home).lanes.push(lane);
        // A press tied to another computer (a Helix preset on the Mac): that computer gets the lane with those steps.
        const tied = new Map<string, { cfg: BridgeConfig; steps: TimelineStep[] }>();
        for (const step of lane.steps) {
          if (!isButtonRefStep(step)) continue;
          const item = findLayoutItemById(step.buttonId);
          if (!item || !isFireableItem(item) || !canPlayFromTimeline(item)) continue;
          const p = itemPlacement(item);
          if (p.kind !== 'bound') continue;
          for (const cfg of p.bridges) {
            if (cfg.id === home.id) continue;
            const e = tied.get(cfg.id) ?? { cfg, steps: [] };
            e.steps.push(step);
            tied.set(cfg.id, e);
          }
        }
        for (const { cfg, steps } of tied.values()) share(cfg).lanes.push({ ...lane, steps });
        continue;
      }
      if (owners.length === 0) { share(home).lanes.push(lane); continue; }
      for (const cfg of owners) share(cfg).lanes.push(lane);
      // An owner that is off: every computer that is on previews its lights.
      if ((isLightsLane(lane) || isInstrumentLane(lane)) && owners.some((o) => !isUp(o))) {
        for (const cfg of up) {
          if (owners.some((o) => o.id === cfg.id)) continue;
          const s = share(cfg);
          s.lanes.push(lane);
          s.previews.push(lane.name);
        }
      }
    }

    const kept = [...shares.values()].filter((s) => s.cfg.id === home.id || s.lanes.some((l) => l.steps.length > 0));
    const ids = kept.map((s) => s.cfg.id);
    const { testTrack: _song, ...withoutSong } = t;
    // Solo is a rule of the WHOLE timeline (audit 2026-11-03 C-5). Each part
    // used to be compiled with only its own lanes, so a solo on the Mac's lane
    // left STREAMPC's lights playing (its part held no solo, so nothing there
    // was silenced) while the editor's preview went quiet. With a solo
    // anywhere, each lane goes as heard in the whole: a silenced one muted, and
    // the solo flags dropped so no part re-judges them from its own lanes.
    const solo = t.lanes.some((l) => l.solo) || !!t.testTrack?.solo;
    const heard = (lane: TimelineLane): TimelineLane => {
      if (!solo) return lane;
      const { solo: _s, ...rest } = lane;
      return laneAudible(t, lane) ? rest : { ...rest, mute: true };
    };
    const homeSong = (): Partial<Timeline> => {
      if (!solo || !t.testTrack) return {};
      const { solo: _s, ...song } = t.testTrack;
      return { testTrack: songAudible(t) ? song : { ...song, mute: true } };
    };
    const parts: TimelinePart[] = kept.map((s) => {
      const isHome = s.cfg.id === home.id;
      return {
        cfg: s.cfg,
        def: isHome ? { ...t, ...homeSong(), lanes: s.lanes.map(heard) } : { ...withoutSong, lanes: s.lanes.map(heard) },
        followsHere: isHome,
        siblings: new Set(ids.filter((id) => id !== s.cfg.id)),
        previews: s.previews,
      };
    });
    const previewed = new Set(parts.filter((p) => isUp(p.cfg)).flatMap((p) => p.previews));
    const offline = parts.filter((p) => !isUp(p.cfg)).map((p) => {
      const names = p.def.lanes.filter((l) => l.steps.length > 0).map((l) => l.name);
      return { cfg: p.cfg, silent: names.filter((n) => !previewed.has(n)), previewed: names.filter((n) => previewed.has(n)) };
    });
    return { home, parts, offline };
  });
}

/** Where a press for `t` goes (a start, a stop, a jump) and where its run is read from: ONE computer holding a part,
 *  and OPBridge carries the press to the rest. The part already running it (so a stop reaches the run on screen), else
 *  the home while it is connected, else another part that is (the primary first). Null when nothing is paired. */
export function timelinePressTarget(t: Timeline): BridgeConfig | null {
  const { home, parts } = timelineParts(t);
  const live = parts.map((p) => p.cfg).filter(isUp);
  const running = live.find((cfg) => transportRun(cfg.id)?.timelineId === t.id);
  if (running) return running;
  if (isUp(home)) return home;
  const primary = primaryBridge();
  return live.find((cfg) => cfg.id === primary?.id) ?? live[0] ?? home;
}

/** What the connected parts of `t` say will not play, from a local compile with the cached port lists (a launch that
 *  skips the push still names it). Null when nothing is dropped. */
export function timelinePartsDropWarning(t: Timeline): string | null {
  const ws = timelineParts(t).parts.filter((p) => isUp(p.cfg))
    .map((p) => toBridgeTimeline(p.def, p.cfg, { followsHere: p.followsHere, siblings: p.siblings, knownPorts: knownPortsFor(p.cfg.id) }).droppedWarning)
    .filter((w): w is string => !!w);
  return ws.length > 0 ? ws.join(' ') : null;
}

/** One sentence per computer holding a part that is off (operator, 2026-10-28: "the rest plays", and say what is
 *  missing): what plays nowhere, and the lights the computers that are on show in OPStage instead. Null when all are on. */
export function timelineOfflineNote(t: Timeline): string | null {
  const off = timelineParts(t).offline;
  if (off.length === 0) return null;
  const list = (names: string[]) => (names.length <= 2 ? names.join(' and ') : `${names.slice(0, 2).join(', ')} and ${names.length - 2} more`);
  return off.map(({ cfg, silent, previewed }) => {
    const who = machineName(cfg);
    if (silent.length === 0 && previewed.length > 0) return `${who} is off: its lights show in OPStage only.`;
    if (previewed.length === 0) return `${who} is off: ${list(silent)} won't play.`;
    return `${who} is off: ${list(silent)} won't play, and its lights show in OPStage only.`;
  }).join(' ');
}

/**
 * A launch's push failures, split by whether the start can go ahead without them.
 *
 * Operator, 2026-10-28: a computer holding a part being OFF does not stop the rest. A computer that is ON but refused
 * or failed its part (a 429, a timeout, a 409 lease) used to stop the whole show from starting, even the song on the
 * computer the press goes to (2026-11-03 audit C-2). Only a failure on a computer the press goes to blocks: its part is
 * the one that starts the run. Every other one is `missed`, and the toast names it.
 */
export function splitPushFailures(results: FireResult[], starters: string[]): { blocking: FireResult[]; missed: FireResult[] } {
  const starterIds = new Set(starters.map((s) => resolveBridge(s)?.id ?? s));
  const failed = results.filter((r) => r.kind !== 'ok');
  const blocking = failed.filter((r) => starterIds.has(r.target) || !bridges().some((b) => b.id === r.target));
  return { blocking, missed: failed.filter((r) => !blocking.includes(r)) };
}

/** "<computer> did not get its part (<why>): it won't play there." for each missed part, or null. */
export function missedPartsNote(missed: FireResult[]): string | null {
  if (missed.length === 0) return null;
  return missed.map((r) => {
    const cfg = bridges().find((b) => b.id === r.target);
    return `${cfg ? machineName(cfg) : r.target} did not get its part (${plainFireReason(r)}): it won't play there.`;
  }).join(' ');
}

/** The dispatch target for a timeline's presses and its transport ({@link timelinePressTarget}): a row id, or `''`
 *  when nothing is paired (every dispatch function already turns that into a `no-bridges` result). */
export function timelineHomeTarget(t: Timeline): string {
  return timelinePressTarget(t)?.id ?? '';
}

/** The definition a Timeline Launcher plays: the timeline, carrying the
 *  launcher's Loop setting when it overrides the timeline's own (`inherit`
 *  follows the timeline). OPBridge's `start` takes only an id and captures the
 *  pushed definition, so the loop must travel IN the pushed definition.
 *  Shared by the on-screen launch (`TimelineLauncher.tsx`) and a mod's share
 *  (`modShare/ops.ts`): before 2026-10-09 the share pushed the timeline as
 *  stored, so a pedal-bound launcher with "Loop: On" over a non-looping
 *  section played it once and stopped. */
export function launcherDefinition(t: Timeline, loop: 'inherit' | 'on' | 'off' | undefined): Timeline {
  const l = loop ?? 'inherit';
  const loops = l === 'on' ? true : l === 'off' ? false : timelineLoops(t);
  return loops === timelineLoops(t) ? t : { ...t, loop: loops };
}

/** Where a Timeline Launcher's launch goes: its own "Sends to" when the
 *  operator set one, else the timeline's home (the machine its devices and
 *  lights are on, DEC-224). The on-screen launch and a mod's share both use
 *  this; the share used to register on the default bridge instead, which put
 *  a lighting launcher on a computer that owns no lights. */
export function launcherTargets(t: Timeline, bridgeIds: string[] | undefined): string[] {
  const explicit = bridgeIds !== undefined && bridgeIds.some((id) => id !== '');
  return explicit ? bridgeIds! : [timelineHomeTarget(t)];
}

// ============================================================
// Port check - is a cue's MIDI port on the bridge it plays on?
// ============================================================

/** OPBridge's own port names (`SYSTEM_CABLE_NAMES` plus `DEFAULT_ABLETON_PORT`
 *  in opbridge-protocol). Always treated as present: on macOS they are
 *  CoreMIDI virtual SOURCES that `GET /midi/ports` never lists as outputs. */
const OPBRIDGE_BUILTIN_PORTS: ReadonlySet<string> = new Set(['OPButtons', 'OPButtons-Control', 'OPBridge-Ableton']);

/** Last port list read from each bridge (row id -> names). A cue's port counts
 *  as present when it is an output, an input (a macOS virtual source shows up
 *  only there), or a built-in name. Deliberately generous: this list only
 *  ever produces a WARNING, never removes a cue, so a false "missing" costs a
 *  wrong sentence while a false "present" costs nothing that today does not. */
const knownPortsByBridge = new Map<string, { names: ReadonlySet<string>; readAt: number }>();

/** How long a port list is trusted before a push re-reads it. Ports change
 *  when a device is plugged in, which is rare; a push (autosync fires one per
 *  edit) must not pay an enumeration round-trip every time, and a Launch must
 *  not wait on one (OPBridge enumerates on Tier 3, of unbounded duration). */
const PORT_LIST_TTL_MS = 15_000;

/** The cached port list for a bridge, or `null` when it has never been read. */
export function knownPortsFor(bridgeId: string): ReadonlySet<string> | null {
  return knownPortsByBridge.get(bridgeId)?.names ?? null;
}

/** Read `GET /midi/ports` from `cfg` and cache it, unless a read younger than
 *  {@link PORT_LIST_TTL_MS} is cached. Falls back to the cached list (or
 *  `null`) when the read fails, so a slow bridge never blocks a push. */
export async function refreshKnownPorts(cfg: BridgeConfig): Promise<ReadonlySet<string> | null> {
  const cached = knownPortsByBridge.get(cfg.id);
  if (cached && Date.now() - cached.readAt < PORT_LIST_TTL_MS) return cached.names;
  const conn = getConnection(cfg.id);
  if (!conn || conn.status() !== 'connected') return knownPortsFor(cfg.id);
  try {
    const { outputs, inputs } = await conn.httpClient().midiPorts();
    const names = new Set<string>([...OPBRIDGE_BUILTIN_PORTS, ...outputs, ...(inputs ?? [])]);
    knownPortsByBridge.set(cfg.id, { names, readAt: Date.now() });
    return names;
  } catch {
    return knownPortsFor(cfg.id);
  }
}

function midiPortOf(action: RuleAction): string | null {
  if (action.module !== 'midi') return null;
  const port = (action.args as { port?: unknown } | undefined)?.port;
  return typeof port === 'string' ? port : null;
}

/** A readable machine name for warnings. */
function machineName(cfg: BridgeConfig | undefined): string {
  return cfg?.name?.trim() || 'that bridge';
}

/** The name a step is called by in a warning - the palette's own tile names. */
function stepItemLabel(item: FireableItem): string {
  switch (item.kind) {
    case 'presetChange': return 'Guitar FX Preset';
    case 'snapshotChange': return 'Helix Snapshot';
    case 'streamdeck': return 'Stream Deck';
    case 'button': return 'Button';
  }
}

function midiSendRaw(port: string, bytes: number[]): RuleAction {
  return { module: 'midi', command: 'sendRaw', args: { port, bytes } };
}

/**
 * One OSC Cue action aimed at the OP-AbletonSync device.
 *
 * ⚠️ `target` is ALWAYS explicit and must stay that way. Byte-verified
 * against `rules_wiring.rs::fire_module_action`: when `args.target` is
 * absent OPBridge does NOT drop the action, it substitutes
 * `"127.0.0.1:7001"` and sends anyway. So an omitted target is not a loud
 * failure - it is a datagram delivered to the wrong port with a cheerful
 * `ok: true`, which is the same silent-wrong-destination class as the
 * missing-`port` MIDI drop this file's module doc already warns about.
 *
 * ⚠️ `command` must be exactly `"send"`. `"sendRaw"` is MIDI-only; the osc
 * arm warns "unknown osc command" and drops.
 *
 * Arg typing is OPBridge's `json_to_osc_args`: a JSON integer becomes
 * OSC int32, a non-integer number becomes float32, a string becomes an OSC
 * string. That matches what every address here wants - ints for the time
 * signature pair, a symbol for the two quantization labels, 0/1 for the
 * metronome. A whole-number BPM therefore crosses as an int rather than a
 * float, which the device's `clip 20. 999.` accepts (verified live: a
 * `123` integer send moved Ableton's tempo).
 */
function abletonOscSend(
  address: string,
  args: Array<number | string>,
  // Every address in this file but the resync one lives on the OPBridge
  // host's loopback. Explicit at the single call site that does not, so the
  // machine change is visible where it happens.
  target: string = ABLETON_SYNC_TARGET,
): RuleAction {
  return {
    module: 'osc',
    command: 'send',
    args: { address, args, target },
  };
}

/**
 * Resolve a button's Ableton Control chain into ordered Cue actions.
 *
 * Two kinds of step CANNOT work from a timeline, and both are dropped with
 * a warning rather than pushed and left to fail quietly:
 *
 *  1. **`clock: 'practice'`** - the practice clock is a set of Solid signals
 *     inside the browser. OPBridge runs timelines with no browser in the
 *     loop and could not drive it even if the tab were open. Pushing these
 *     would produce a timeline that works when tapped by hand and does
 *     nothing when played, which is the worst possible failure shape.
 *
 *  2. **metronome `'toggle'`** - a toggle needs the state at FIRE time.
 *     Resolving it here would freeze "the opposite of whatever it was at
 *     push time", so a timeline played twice would flip the click on the
 *     first pass and off on the second. Explicit On/Off is expressible and
 *     correct; a frozen toggle is neither.
 *
 * Invalid quantization labels are refused for the same reason they are
 * refused in `dispatch.ts` - an unknown label is a silent no-op device-side
 * that only prints to the Max Console.
 */
function abletonStepsToActions(steps: AbletonStep[], beatsPerBar = 4): RuleAction[] {
  const out: RuleAction[] = [];
  for (const step of steps) {
    if ('clock' in step && step.clock === 'practice') {
      warnAbletonStepDropped(step.type, 'targets the practice clock, which only exists in the browser');
      continue;
    }
    switch (step.type) {
      case 'practice.transport':
        // Browser-only, like every practice-targeted step. Its own kind
        // rather than a toggle on the Ableton transport (the verbs are
        // inverted between the clocks), so it drops here for the same
        // reason and with its own message.
        warnAbletonStepDropped(step.type, 'drives the practice clock, which only exists in the browser');
        break;
      case 'ableton.tempo':
        out.push(abletonOscSend(ABLETON_TEMPO_ADDRESS, [clampTempo(step.bpm)]));
        break;
      case 'ableton.timesig':
        out.push(abletonOscSend(ABLETON_TIME_SIG_ADDRESS, [
          clampInt(step.numerator, 1, 99),
          clampInt(step.denominator, 1, 32),
        ]));
        break;
      case 'ableton.transport':
        // ⚠️ Under an ABLETON master, `play` and `continue` in a timeline are
        // close to unreachable by construction: a timeline playhead only
        // advances while the clock is RUNNING, so a step that starts the
        // clock can never be reached to fire. Observed directly while
        // proving this path - firing an Action timeline with the transport
        // stopped returned 200 with an instanceId and `activeActionIds: []`,
        // and nothing happened. And if the clock IS running, `play` RESETS
        // Ableton to the top, yanking the very clock the timeline rides.
        //
        // `stop` is the one that is genuinely useful here.
        //
        // Still PUSHED rather than dropped, deliberately: under an internal
        // or Link master these are coherent (one clock's timeline driving
        // another's transport), and silently deleting an authored step on an
        // assumption about which clock is mastering is worse than a step
        // that no-ops on one rig. OPBridge chose not to offer transport
        // steps at all on their surface; we warn instead of deciding for the
        // operator. Revisit if this proves confusing in practice.
        if (step.action !== 'stop') {
          warnAbletonStepDropped(
            `${step.type}:${step.action}`,
            'pushed, but under an Ableton master a timeline cannot reach a step that starts the clock - only "stop" is reliably useful in a timeline',
          );
        }
        out.push(abletonOscSend(
          step.action === 'play' ? ABLETON_PLAY_ADDRESS
            : step.action === 'stop' ? ABLETON_STOP_ADDRESS
              : ABLETON_CONTINUE_ADDRESS,
          [],
        ));
        break;
      case 'ableton.recquant': {
        const label = normalizeRecQuant(step.label);
        if (label === null) { warnAbletonStepDropped(step.type, `"${step.label}" is not a record-quantization label`); break; }
        // The same packets a tap sends (`recQuantSends`): an OP-Quantize step
        // sets Live's own quantize to None first, and never sends Live a grid.
        for (const o of recQuantSends(label, step.via)) out.push(abletonOscSend(o.address, o.args, o.target));
        break;
      }
      case 'ableton.launchquant': {
        const label = normalizeLaunchQuant(step.label);
        if (label === null) { warnAbletonStepDropped(step.type, `"${step.label}" is not a launch-quantization label`); break; }
        out.push(abletonOscSend(ABLETON_LAUNCH_QUANT_ADDRESS, [label]));
        break;
      }
      case 'ableton.metronome':
        if (step.mode === 'toggle') {
          warnAbletonStepDropped(step.type, 'toggle needs the state at fire time; use explicit On or Off in a timeline');
          break;
        }
        out.push(abletonOscSend(ABLETON_METRONOME_ADDRESS, [step.mode === 'on' ? 1 : 0]));
        break;
      // Always off, so a timeline can end a take on a bar line (no state needed at fire time, unlike a toggle).
      case 'ableton.endsessionrecord':
        out.push(abletonOscSend(ABLETON_END_SESSION_RECORD_ADDRESS, []));
        break;
      // OP-Motion's crossfader (2026-10-23): a flick or a fade, the fade's bars counted as OPBridge counts a bar.
      case 'opmotion.crossfade': {
        const x = crossfadeSend(step, beatsPerBar);
        out.push(abletonOscSend(x.address, x.args, MOTION_OSC_TARGET));
        break;
      }
      // A landing preset (2026-10-24): its stored pairs, read NOW, one land per bus. OP-Motion arrives on the next
      // downbeat after the cue. Recapturing it is a project edit, which re-pushes the timeline (`autoSync.ts`).
      case 'opmotion.land': {
        const preset = motionPresetById(step.presetId);
        const sends = preset ? landSends(preset) : [];
        if (sends.length === 0) { warnAbletonStepDropped(step.type, preset ? `preset "${preset.name}" holds nothing to land` : 'no landing preset picked'); break; }
        for (const x of sends) out.push(abletonOscSend(x.address, x.args, MOTION_OSC_TARGET));
        break;
      }
      // A capture needs THIS APP to hear OP-Motion's answer, and a timeline or a pedal rule plays with no app in the
      // loop to store it. Dropped, never pushed: it would ask and nothing would keep the answer.
      case 'opmotion.capture':
        warnAbletonStepDropped(step.type, 'a capture is stored by this app when OP-Motion answers, so it only works as a tap');
        break;
      // Pushed WITH A WARNING rather than dropped, which is this file's
      // existing call on the transport steps: silently deleting a step the
      // operator authored is worse than one that no-ops.
      //
      // The tap path REFUSES an unaimed resync (see `sendAbletonResync`); a
      // timeline cannot, because the cue is executed by whichever bridge it
      // was pushed to, and that resolution happens inside OPBridge long
      // after this code has stopped running. There is also no browser in the
      // loop at play time, so unlike a tap there is nowhere to toast. The
      // console warning at push time is the only channel that exists.
      case 'ableton.resync':
        warnAbletonStepDropped(
          step.type,
          'pushed, but it fires on whichever bridge runs the timeline - it only does anything if that bridge is the one on the slave, and on any other machine it is a silent no-op',
        );
        out.push(abletonOscSend(ABLETON_RESYNC_ADDRESS, [], ABLETON_SYNC_RECEIVE_TARGET));
        break;
      // Same reasoning as resync above: the tap path refuses an unaimed one,
      // a timeline cannot, so it is pushed with the one warning available.
      case 'ableton.sustainkill':
        warnAbletonStepDropped(
          step.type,
          'pushed, but it fires on whichever bridge runs the timeline - it only releases the pedal if that bridge is on the computer running Ableton with OP-SustainStop loaded',
        );
        out.push(abletonOscSend(ABLETON_SUSTAIN_KILL_ADDRESS, [], SUSTAIN_STOP_TARGET));
        break;
    }
  }
  return out;
}

/** Clamp mirrors the device's own `clip 20. 999.`, so a value the device
 *  would silently clamp is never pushed as if it had been honoured. */
function clampTempo(bpm: number): number {
  return Math.max(20, Math.min(999, bpm));
}
function clampInt(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, Math.round(n)));
}

const abletonDropWarned = new Set<string>();
/** Once per (type, reason) - a push happens on every arm tap, and a warning
 *  storm would bury everything else, same reasoning as
 *  `warnDroppedKindOnce`. */
function warnAbletonStepDropped(type: string, reason: string): void {
  const key = `${type}:${reason}`;
  if (abletonDropWarned.has(key)) return;
  abletonDropWarned.add(key);
  console.warn(`[timeline] dropped Ableton step "${type}" - ${reason}`);
}

/** Resolve one fireable item to the ordered `sendRaw` action(s) it fires
 *  when tapped, for firing specifically on `cfg`. `null` = can't fire on
 *  this bridge at all (removed item, unresolvable pedal, item's own
 *  bridge affinity doesn't include `cfg`, or StreamDeck's confirmed gap) —
 *  the caller drops the step rather than push a broken action, matching
 *  `ruleSync.ts::actionFor`'s "refuse rather than send garbage" contract. */
/** The ON actions a step fires at its own bar, plus the OFF actions fired
 *  at the right-hand edge of its block. `off` is empty for everything with
 *  no meaningful release.
 *
 *  **MIDI NOTES ONLY (operator, 2026-08-23: "if the block is a CC, no need
 *  for an off ... so only for midi notes").** An earlier pass here also
 *  released a `cc` block with `[0xb0|ch, cc, 0]`, mirroring
 *  `dispatch.ts::toggleActions`. That was wrong for a Timeline: a toggle
 *  BUTTON has two states and needs its off, but a CC block on a timeline is
 *  a fire-and-forget value set - zeroing it a bar later would actively
 *  destroy the value the operator placed. Only a note-on creates something
 *  that must be released, because only a note-on sustains.
 *
 *  The note-off byte shape still matches `toggleActions`'s release exactly
 *  (`[0x80|ch, note, 0]`) - a Timeline release and a toggle release are the
 *  same physical event to the rig and must not disagree. Built inline
 *  rather than imported because the ON half above already builds its bytes
 *  inline in this file's own style. If `toggleActions` changes shape,
 *  change both. */
export interface FireableItemActions { on: RuleAction[]; off: RuleAction[] }

/** Exported 2026-08-28 so `midiTrigger/ruleSync.ts` resolves a bound
 *  PresetChange/SnapshotChange to the SAME bytes a Timeline step fires.
 *  Shared deliberately: two resolvers for "what does this item do" is how a
 *  pedal press and a timeline cue drift apart, the same reasoning that made
 *  `dispatch.ts::toggleActions` shared rather than copied. */
export function resolveFireableItemActions(
  item: FireableItem,
  cfg: BridgeConfig,
  // `skipBridgeCheck`: the timeline compile decides placement once, up
  // front (`resolveStepToCues`), and must not be second-guessed here - a
  // step with no machine of its own follows the timeline's home, which
  // is not necessarily the primary this check would compare against.
  // Pedal rules (`midiTrigger/ruleSync.ts`) leave it off: unchanged.
  // `beatsPerBar`: a crossfade's bars in beats (a timeline's bar is OPBridge's 4; 4 when absent).
  opts: { skipBridgeCheck?: boolean; beatsPerBar?: number } = {},
): FireableItemActions | null {
  if (item.kind === 'streamdeck') return null;

  if (item.kind === 'presetChange') {
    const targets = itemBridgeTargets(item);
    if (!item.pedalId || (!opts.skipBridgeCheck && !targetsIncludeBridge(targets, cfg))) return null;
    const pedal = scopedPedals().find((pd) => pd.id === item.pedalId);
    const port = pedal?.port;
    if (!pedal || !port) return null;
    const channel = clampNibble(pedal.channel);
    const actions: RuleAction[] = [
      midiSendRaw(port, [0xb0 | channel, 32, clampByte(item.setlist)]), // CC32 setlist
      midiSendRaw(port, [0xc0 | channel, clampByte(item.program)]),    // Program Change
    ];
    if (typeof item.snapshot === 'number') {
      actions.push(midiSendRaw(port, [0xb0 | channel, 69, clampByte(item.snapshot)])); // CC69 snapshot
    }
    // No OFF: a preset change is a state change on the pedal, not a held
    // gesture. Releasing it has no meaning and "reverting" would need a
    // previous-preset concept nothing here tracks.
    return { on: actions, off: [] };
  }

  if (item.kind === 'snapshotChange') {
    const targets = itemBridgeTargets(item);
    if (!item.pedalId || (!opts.skipBridgeCheck && !targetsIncludeBridge(targets, cfg))) return null;
    const pedal = scopedPedals().find((pd) => pd.id === item.pedalId);
    const port = pedal?.port;
    if (!pedal || !port) return null;
    const channel = clampNibble(pedal.channel);
    const value = item.snapshot === 'next' ? 8 : item.snapshot === 'prev' ? 9 : clampByte(item.snapshot);
    // No OFF, same reasoning as presetChange above.
    return { on: [midiSendRaw(port, [0xb0 | channel, 69, value])], off: [] };
  }

  // ButtonItem — plain button, or a pedal-bound "Custom pedal" Button
  // (DEC-152's `pedalId` extension). Fires the ON action only: a Cue is a
  // single discrete trigger (PaletteFireableItem's own doc-comment
  // contract), not the toggle latch a manual tap can also express.
  const targets = itemBridgeTargets(item);
  if (!opts.skipBridgeCheck && !targetsIncludeBridge(targets, cfg)) return null;

  // Ableton Control — resolved BEFORE the MIDI port lookup below, because
  // this button needs no MIDI port at all. Falling through would hit
  // `if (!port) return null` and silently drop every Ableton step on a rig
  // with no default MIDI output configured.
  if (item.actionType === 'ableton') {
    const actions = abletonStepsToActions(item.abletonSteps ?? [], opts.beatsPerBar);
    return actions.length === 0 ? null : { on: actions, off: [] };
  }
  // A Hue scene (2026-10-23, OPBridge S331): one module action and no MIDI port, so also before the port lookup.
  if (item.actionType === 'hueScene') {
    const hs = item.hueScene;
    return hs?.scene ? { on: [hueSceneAction(hs)], off: [] } : null;
  }
  const pedal = item.pedalId ? scopedPedals().find((pd) => pd.id === item.pedalId) : undefined;
  if (item.pedalId && !pedal) return null; // pedal removed - dead, per DEC-138
  // Cue actions have no bridge-side default-port fallback (see module doc
  // comment) - an ordinary button's implicit "rig default" must be
  // resolved to a concrete name HERE, from the bridge we're pushing to.
  const port = pedal?.port ?? cfg.defaultMidiOutput;
  if (!port) return null;
  const channel = clampNibble((pedal ? pedal.channel : item.channel));
  const action = item.actionType ?? 'cc';
  if (action === 'pc') {
    // A Program Change is two bytes with no value and no OFF counterpart
    // (ButtonItem.program's own schema comment says so) - a `pc` block stays
    // instant however long the operator draws it.
    return { on: [midiSendRaw(port, [0xc0 | channel, clampByte(item.program ?? 0)])], off: [] };
  }
  if (action === 'noteon') {
    const note = clampByte(item.note ?? 60);
    const velocity = clampByte(item.velocity ?? 127);
    return {
      on: [midiSendRaw(port, [0x90 | channel, note, velocity])],
      off: [midiSendRaw(port, [0x80 | channel, note, 0])],
    };
  }
  // CC: no release. Operator-specified - a CC block sets a value, it does
  // not hold one, so there is nothing to let go of.
  return {
    on: [midiSendRaw(port, [0xb0 | channel, clampByte(item.ccNumber), clampByte(item.onValue)])],
    off: [],
  };
}

// ============================================================
// Wire translation — PWA Timeline -> OPBridge Timeline, for one bridge
// ============================================================

/**
 * The operator's name for a step kind OPBridge's timeline engine cannot run
 * yet, or `null` for one that is never meant to fire (a marker is a note for
 * the performer).
 *
 * The engine understands only `cue` steps (DMX animation/M4, sequencer and
 * macro placement/M6 and value steps have no runtime there yet). These used
 * to go to the console only, so a light colour drawn on a timeline simply
 * never happened and nothing said so; since plan 3.3 (2026-10-01) they are
 * named like every other step that cannot play - in the push warning, the
 * sync pill and on the step itself (`timelineStepWarnings`).
 */
function unplayableKindLabel(step: TimelineStep): string | null {
  switch (step.kind) {
    case 'animation': return 'Light FX';
    case 'colorMarker': return 'Light colour';
    case 'lightRelease': return 'Light colour';
    case 'sequencer': return 'Sequencer';
    case 'macro': return 'Macro';
    case 'targetValue':
      switch (step.targetId) {
        case 'transpose': return 'Transpose';
        case 'capo': return 'Capo';
        case 'autotuneKey': return 'Waves key';
        case 'autotuneScale': return 'Waves scale';
        default: return 'Value';
      }
    case 'marker': return null;
    case 'automationPoint': return null;   // it plays: an automation lane compiles to CC cues (automationCues)
    case 'hitRoute': case 'hitBlock': return 'Drum lights';
    default: return 'Unknown';
  }
}

/** The channel (0-based) and CC a fader, a knob, or one axis of an XY pad sends; null for any other item. */
function ccOfControl(item: LayoutItem | undefined, axis: 'x' | 'y' | undefined): { channel: number; cc: number } | null {
  if (!item) return null;
  if (item.kind === 'fader' || item.kind === 'radial') return { channel: clampNibble(item.channel), cc: clampByte(item.ccNumber) };
  if (item.kind === 'xypad') {
    return axis === 'y'
      ? { channel: clampNibble(item.yChannel), cc: clampByte(item.yCcNumber) }
      : { channel: clampNibble(item.xChannel), cc: clampByte(item.xCcNumber) };
  }
  return null;
}

/** An automation lane as CC cues (2026-10-16, record mode's fader moves): its control's own channel and CC on this
 *  bridge's default output, as the control itself sends. Each point sends its value; a `linear` segment to the next
 *  point is sampled every sixteenth and sent only when the 7-bit value changes (thinned: a held fader sends nothing);
 *  `hold` waits for the next point. */
function automationCues(lane: TimelineLane, cfg: BridgeConfig, sig: number, dropped: DroppedStep[], t: Timeline): WireCue[] {
  const pts = lane.steps
    .filter((x): x is AutomationPointStep => x.kind === 'automationPoint')
    .sort((a, b) => a.position - b.position);
  if (pts.length === 0) return [];
  if (lane.vizKnob) return vizKnobCues(lane.vizKnob, pts, cfg, sig, dropped);
  if (lane.opvControl) return opvControlCues(lane.opvControl, pts, sig, dropped);
  const send = ccOfControl(lane.control ? findLayoutItemById(lane.control.itemId) : undefined, lane.control?.axis);
  if (!send) { dropped.push({ kind: 'missing-item', stepId: pts[0]!.id }); return []; }
  const port = cfg.defaultMidiOutput;
  if (!port) { dropped.push({ kind: 'no-port', label: 'Automation', machine: machineName(cfg), stepId: pts[0]!.id }); return []; }
  const out: WireCue[] = [];
  let last = -1;
  const emit = (id: string, at: number, v: number) => {
    const val = clampByte(Math.round(v));
    if (val === last) return;
    last = val;
    out.push({ id, positionBars: at, kind: 'cue', action: midiSendRaw(port, [0xb0 | send.channel, send.cc, val]) });
  };
  const step = 1 / (Math.max(1, sig) * 4);
  pts.forEach((pt, i) => {
    emit(pt.id, pt.position, pt.value);
    const next = pts[i + 1];
    if (!next || (pt.curve ?? 'linear') === 'hold' || next.position <= pt.position) return;
    let k = 1;
    for (let at = pt.position + step; at < next.position - 1e-9; at += step, k++) {
      emit(`${pt.id}:${k}`, at, pt.value + (next.value - pt.value) * shapeAt(pt.curve, (at - pt.position) / (next.position - pt.position)));
    }
  });
  return withSectionCatchUp(out, pts, t, lane.id, (v) => midiSendRaw(port, [0xb0 | send.channel, send.cc, clampByte(Math.round(v))]));
}

/**
 * An automation lane's cues, made right for a playback that does not start at bar 0 (2026-11-03 audit C-1).
 *
 * The compile sends a value only where it CHANGES. OPBridge, re-anchoring a looping section, a jump, a seek or a start
 * from a bar, marks every step before that bar as passed (opbridge-timelines `reanchor_to`). So a CHORUS looping at
 * bars 8-16, with a point at 6 holding 20 and one at 12 setting 100, played 20 for bars 8-12 on its first pass and
 * stayed at 100 on every pass after: nothing was sent at bar 8. Same after any jump. OP-Motion lanes already add the
 * curve's value at each section start (`opMotion.ts`); this does the same for CC lanes: at every section start where
 * the curve has a value, a cue with that value, sent even when it equals the last one (that is the point of it).
 * Points in the lead-in are dropped, as everywhere else ("steps left there are not sent"); the first section's start
 * carries the curve's value instead.
 */
function withSectionCatchUp(
  out: WireCue[],
  pts: readonly AutomationPointStep[],
  t: Timeline,
  laneId: string,
  action: (v: number) => WireCue['action'],
): WireCue[] {
  const lead = timelineLeadIn(t);
  const kept = lead > 0 ? out.filter((c) => c.positionBars >= lead - 1e-9) : out;
  const starts: number[] = [];
  let at = 0;
  for (const sec of timelinePlaySections(t)) {
    if (sec.id !== leadInSectionId(t) && sec.lengthBars > 0) starts.push(at);
    at += sec.lengthBars;
  }
  const add: WireCue[] = [];
  for (const s of starts) {
    if (s < pts[0]!.position - 1e-9) continue; // before the first point the lane has said nothing
    if (kept.some((c) => Math.abs(c.positionBars - s) < 1e-9)) continue; // a cue already lands on the line
    const v = autoValueAtExact(pts, s);
    if (v === null) continue;
    add.push({ id: `${laneId}@${s}`, positionBars: s, kind: 'cue', action: action(v) });
  }
  if (add.length === 0) return kept;
  return [...kept, ...add].sort((a, b) => a.positionBars - b.positionBars);
}

/** A CHAIN on the wire (2026-10-23, `timeline/chain.ts`; OPBridge PROTOCOL "Chains", S307): `"tier": "chain"`, no
 *  sections, ONE lane whose steps go in run order - each named action as a `cue` (OPVisual's cells and columns with
 *  Follow off, as on any timeline), each wait as `{kind: "wait", unit, amount?}`. Nothing is sent to a bridge without
 *  `timeline_chains` (it would play the steps all at once), to one whose waits it would refuse, or with no action in
 *  it (OPBridge: "A chain needs at least one action"); each says why. */
function chainWire(t: Timeline, cfg: BridgeConfig): WireTimelineResult {
  const steps = chainSteps(t);
  const none = (why: string): WireTimelineResult => ({ wire: null, droppedWarning: why, stepWarnings: new Map(steps.map((s) => [s.id, why])) });
  if (!bridgePlaysChains(cfg)) return none(`This chain waits for OPBridge on ${machineName(cfg)} to update (it plays chains from S307)`);
  const out: Record<string, unknown>[] = [];
  const stepWarnings = new Map<string, string>();
  for (const s of steps) {
    if (isWaitStep(s)) {
      const bad = waitProblem(s);
      if (bad) { stepWarnings.set(s.id, bad); continue; }
      out.push(s.unit === 'nextBar' ? { id: s.id, kind: 'wait', unit: 'nextBar' } : { id: s.id, kind: 'wait', unit: s.unit, amount: s.amount });
      continue;
    }
    const a = s.kind === 'cue' ? (s.action as Partial<RuleAction> | undefined) : undefined;
    if (a && typeof a.module === 'string' && typeof a.command === 'string' && a.args && typeof a.args === 'object') {
      out.push({ id: s.id, kind: 'cue', action: withOpvFollowDefault(a as RuleAction) });
      continue;
    }
    stepWarnings.set(s.id, "A chain step must fire an action or wait; this one can't be sent");
  }
  if (!out.some((x) => x['kind'] === 'cue')) {
    const why = 'A chain needs at least one action to fire';
    return { wire: null, droppedWarning: why, stepWarnings };
  }
  const lane = chainLane(t);
  return {
    wire: {
      ...unknownFieldsOf(t, TIMELINE_KNOWN_KEYS),
      id: t.id,
      name: t.name,
      tier: 'chain',
      modified: t.modified,
      origin: timelineOrigin(t),
      ...(Number.isFinite(Date.parse(t.modified)) ? { modifiedAt: Date.parse(t.modified) } : {}),
      sections: [],
      lanes: [{ id: lane.id, name: lane.name, steps: out }],
      loops: false,
    },
    droppedWarning: stepWarnings.size ? `${stepWarnings.size} chain step${stepWarnings.size === 1 ? '' : 's'} can't be sent: ${[...new Set(stepWarnings.values())].join('; ')}` : null,
    stepWarnings,
  };
}

/** The bridge OPVisual listens on (its `/ws/consumer` socket), as this app aims every OPVisual item, or null. */
export function opVisualBridge(): BridgeConfig | null {
  const id = autoPickOpVisualBridge();
  return id ? resolveBridge(id) ?? null : null;
}

/** OPVisual's show colour or show palette lane (2026-10-28): sent to every connected computer, never to the rig. */
export function isOpvShowLane(lane: TimelineLane): boolean {
  return lane.laneKind === 'opvisual-colour' || lane.laneKind === 'opvisual-palette';
}

const opv = (command: string, args: Record<string, unknown>): RuleAction => ({ module: 'opvisual', command, args } as RuleAction);
const r3 = (x: number) => Math.round(Math.min(1, Math.max(0, x)) * 1000) / 1000;

/**
 * An OPVisual colour lane as SHOW COLOUR cues (2026-10-28, OPVisual's ask and its DEC-174; operator: "a lane that sends
 * a colour to opvisual ... if there is no opbuttons colour then the clips are the original look"). OPVisual's CONTRACT
 * 4b: `colour {r, g, b, fade?}` (display sRGB 0..1; `fade` in BEATS, absent = a cut) fades from what is on screen, and
 * `colour_release {fade?}` (absent = one bar) hands every clip back. OPVisual draws a fade on its own clock, so a ramp
 * is ONE message, never samples:
 *  - a keyframe is a cut on its beat, or with a lead-in a fade over the lead-in arriving on its beat;
 *  - a keyframe that is not `hold` ramps to the next one: one `colour(next, fade = the gap)` on its beat, after its own
 *    colour (skipped when a ramp already arrives there);
 *  - a release (`lightRelease`) is `colour_release`, over its lead-in when it has one.
 * White folds into RGB (OPVisual has no white). On every connected computer's copy, like a knob lane (`laneOwners`).
 */
function opvColourCues(steps: TimelineStep[], cfg: BridgeConfig, dropped: DroppedStep[]): WireCue[] {
  const marks = steps
    .filter((x): x is ColorMarkerStep | LightReleaseStep => x.kind === 'colorMarker' || x.kind === 'lightRelease')
    .sort((a, b) => a.position - b.position);
  if (marks.length === 0) return [];
  // Sent on every computer that plays a copy (`laneOwners`): each hands it to its own OPVisual, if it has one.
  void cfg; void dropped;
  const rgb = (c: ColorMarkerStep['color']) => { const w = c.w ?? 0; return { r: r3(c.r + w), g: r3(c.g + w), b: r3(c.b + w) }; };
  const beats = (bars: number) => Math.round(bars * OPBRIDGE_BEATS_PER_BAR * 1000) / 1000;
  const out: WireCue[] = [];
  let rampArrivesAt: number | null = null;
  marks.forEach((m, i) => {
    const lead = m.leadInBars && m.leadInBars > 0 ? m.leadInBars : 0;
    if (m.kind === 'lightRelease') {
      out.push({ id: m.id, positionBars: Math.max(0, m.position - lead), kind: 'cue', action: opv('colour_release', lead ? { fade: beats(lead) } : {}) });
      rampArrivesAt = null;
      return;
    }
    const arrived = rampArrivesAt !== null && Math.abs(rampArrivesAt - m.position) < 1e-9;
    if (!arrived) {
      out.push({ id: m.id, positionBars: Math.max(0, m.position - lead), kind: 'cue', action: opv('colour', { ...rgb(m.color), ...(lead ? { fade: beats(lead) } : {}) }) });
    }
    rampArrivesAt = null;
    const next = marks[i + 1];
    if (next && next.kind === 'colorMarker' && (m.curve ?? 'linear') !== 'hold' && next.position > m.position + 1e-9) {
      out.push({ id: `${m.id}:ramp`, positionBars: m.position, kind: 'cue', action: opv('colour', { ...rgb(next.color), fade: beats(next.position - m.position) }) });
      rampArrivesAt = next.position;
    }
  });
  return out;
}

/** An OPVisual palette lane's steps (2026-10-28): its `palette` cues as they are, on every computer's copy. */
function opvPaletteCues(steps: TimelineStep[], cfg: BridgeConfig, dropped: DroppedStep[]): WireCue[] {
  const cues = steps.filter((x): x is CueStep => x.kind === 'cue').sort((a, b) => a.position - b.position);
  if (cues.length === 0) return [];
  void cfg;
  const out: WireCue[] = [];
  for (const c of cues) {
    const a = c.action as { module?: unknown; command?: unknown; args?: { id?: unknown } } | undefined;
    const id = a?.module === 'opvisual' && a.command === 'palette' && typeof a.args?.id === 'string' ? a.args.id.trim() : '';
    if (!id) { dropped.push({ kind: 'unresolvable', label: 'OPVisual palette (no palette chosen)', stepId: c.id }); continue; }
    out.push({ id: c.id, positionBars: c.position, kind: 'cue', action: opv('palette', { id }) });
  }
  return out;
}

/** Everything a run of `t` on `cfg` fires when it ends (OPBridge S339 `atStop`), at most 64. */
function atStopOf(t: Timeline, cfg: BridgeConfig): RuleAction[] {
  return [...motionAtStop(t, cfg), ...opvColourAtStop(t, cfg), ...opvControlAtStop(t)].slice(0, 64);
}

/** What a run of `t` sends OPVisual when it ENDS, however it ends: a colour lane hands every clip back
 *  (`colour_release`, one bar), so stopping a song never leaves the projector recoloured (OPVisual's ask, item 5). */
export function opvColourAtStop(t: Timeline, cfg: BridgeConfig): RuleAction[] {
  // `t` is this computer's own part: it carries the colour lane wherever an OPVisual may be listening.
  void cfg;
  const has = t.lanes.some((l) => l.laneKind === 'opvisual-colour' && laneAudible(t, l) && l.steps.some((x) => x.kind === 'colorMarker'));
  return has ? [opv('colour_release', {})] : [];
}

/** An OPVisual control lane as cues (2026-11-02, `opvControls.ts`): a value sampled and thinned as a knob lane is, an
 *  on/off only where the curve crosses its middle, a fire once per point. On every computer's copy, like a knob lane. */
function opvControlCues(c: NonNullable<TimelineLane['opvControl']>, pts: AutomationPointStep[], sig: number, dropped: DroppedStep[]): WireCue[] {
  const def = opvControlDef(c);
  if (!def) { dropped.push({ kind: 'unresolvable', label: `OPVisual ${c.verb}`, stepId: pts[0]!.id }); return []; }
  if (def.cell) return opvCellFxCues(def, c, pts, sig, dropped);
  const base: Record<string, unknown> = { ...(def.param ? { param: def.param } : {}), ...(def.layered ? { layer: Math.min(5, Math.max(1, Math.round(c.layer ?? 1))) } : {}) };
  const cue = (id: string, at: number, extra: Record<string, unknown>): WireCue => ({ id, positionBars: at, kind: 'cue', action: opv(def.verb, { ...base, ...extra }) });
  if (def.mode === 'fire') return pts.map((p) => cue(p.id, p.position, {}));
  const out: WireCue[] = [];
  if (def.mode === 'toggle') {
    let last: boolean | null = null;
    for (const p of pts) {
      const on = p.value >= 64;
      if (on !== last) out.push(cue(p.id, p.position, { on }));
      last = on;
    }
    return out;
  }
  for (const v of valueSamples(pts, sig, def.min ?? 0, def.max ?? 1)) out.push(cue(v.id, v.at, { value: v.value }));
  return out;
}

/** A value curve as the values to send: each point, then every sixteenth of a shaped segment, kept only when its 7-bit
 *  value changes, scaled to `min`..`max` (3 decimals). Shared by the OPVisual value controls and the effect amount. */
function valueSamples(pts: AutomationPointStep[], sig: number, min: number, max: number): Array<{ id: string; at: number; value: number }> {
  const out: Array<{ id: string; at: number; value: number }> = [];
  let last = -1;
  const emit = (id: string, at: number, v: number) => {
    const val = clampByte(Math.round(v));
    if (val === last) return;
    last = val;
    out.push({ id, at, value: Math.round((min + (val / 127) * (max - min)) * 1000) / 1000 });
  };
  const step = 1 / (Math.max(1, sig) * 4);
  pts.forEach((pt, i) => {
    emit(pt.id, pt.position, pt.value);
    const next = pts[i + 1];
    if (!next || (pt.curve ?? 'linear') === 'hold' || next.position <= pt.position) return;
    let k = 1;
    for (let at = pt.position + step; at < next.position - 1e-9; at += step, k++) {
      emit(`${pt.id}:${k}`, at, pt.value + (next.value - pt.value) * shapeAt(pt.curve, (at - pt.position) / (next.position - pt.position)));
    }
  });
  return out;
}

/** An "Effect on a cell" lane as cues (2026-11-02, OPVisual's ask; its CONTRACT s4b): a toggle sends `fx` with its
 *  effect where the curve crosses into the top half and `fx_release` where it crosses out; the amount lane sends `fx`
 *  with `amount` 0..1, sampled and thinned as a value control is. Both name the cell as OPVisual's grid labels it. */
function opvCellFxCues(def: OpvControlDef, c: NonNullable<TimelineLane['opvControl']>, pts: AutomationPointStep[], sig: number, dropped: DroppedStep[]): WireCue[] {
  if (!c.cell) { dropped.push({ kind: 'unresolvable', label: `OPVisual ${def.label} (no cell)`, stepId: pts[0]!.id }); return []; }
  const cell = opvCellLabel(c.cell);
  if (def.mode === 'toggle') {
    const out: WireCue[] = [];
    let last: boolean | null = null;
    for (const p of pts) {
      const on = p.value >= 64;
      // A lane that starts in the bottom half has nothing to let go of yet.
      if (on !== last && (on || last !== null)) {
        out.push({ id: p.id, positionBars: p.position, kind: 'cue', action: on ? opv('fx', { cell, fx: def.param }) : opv('fx_release', { cell }) });
      }
      last = on;
    }
    return out;
  }
  // The amount, 0..1: sampled as any value control is.
  return valueSamples(pts, sig, 0, 1).map((v) => ({ id: v.id, positionBars: v.at, kind: 'cue' as const, action: opv('fx', { cell, amount: v.value }) }));
}

/** What a stop sends for the OPVisual controls a run drove (`OpvControlDef.atStop`), so stopping mid-blackout or
 *  mid-fade never leaves the projector dark. Each lane's control once. An effect on a cell is let go of (OPVisual
 *  cannot see a timeline stop and would keep holding it, its ask), once per cell. */
export function opvControlAtStop(t: Timeline): RuleAction[] {
  const seen = new Set<string>();
  const out: RuleAction[] = [];
  for (const l of t.lanes) {
    if (l.laneKind !== 'automation' || !l.opvControl || !laneAudible(t, l) || !l.steps.some((x) => x.kind === 'automationPoint')) continue;
    const def = opvControlDef(l.opvControl);
    if (def?.cell) {
      if (!l.opvControl.cell) continue;
      const cell = opvCellLabel(l.opvControl.cell);
      if (seen.has(`fx:${cell}`)) continue;
      seen.add(`fx:${cell}`);
      out.push(opv('fx_release', { cell }));
      continue;
    }
    if (!def?.atStop) continue;
    const layer = def.layered ? Math.min(5, Math.max(1, Math.round(l.opvControl.layer ?? 1))) : undefined;
    const key = `${def.key}:${layer ?? ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(opv(def.verb, { ...(layer !== undefined ? { layer } : {}), ...def.atStop }));
  }
  return out;
}

/** An OPVisual knob lane as `viz_macro` cues (2026-10-23, OPVisual's ask): `{module: "opvisual", command:
 *  "viz_macro", args: {macro, value 0..1, layer?}}`, which OPBridge publishes to OPVisual's socket exactly as a rule
 *  does (OPBridge PROTOCOL "OPVisual control and state", `consumer_ws.rs::opvisual_trigger_payload`). Sampled and
 *  thinned like a control's lane: each point, then every sixteenth of a `linear` segment, sent only when its 7-bit
 *  value changes. A module action is published on the bridge that plays it, not carried across the mesh, so since
 *  2026-10-28 the lane goes to every connected computer's copy (`laneOwners`) and each OPVisual gets it from its own
 *  OPBridge (OPVisual's ask "send to every OPVisual"). */
function vizKnobCues(
  knob: { macro: number; layer?: number }, pts: AutomationPointStep[], cfg: BridgeConfig, sig: number,
  dropped: DroppedStep[],
): WireCue[] {
  // Sent on every computer that plays a copy (2026-10-28, `laneOwners`): each hands it to its own OPVisual.
  void cfg; void dropped;
  const macro = Math.min(7, Math.max(1, Math.round(knob.macro)));
  const layer = knob.layer !== undefined ? Math.min(5, Math.max(1, Math.round(knob.layer))) : undefined;
  const out: WireCue[] = [];
  let last = -1;
  const emit = (id: string, at: number, v: number) => {
    const val = clampByte(Math.round(v));
    if (val === last) return;
    last = val;
    const args: Record<string, unknown> = { macro, value: Math.round((val / 127) * 1000) / 1000 };
    if (layer !== undefined) args.layer = layer;
    out.push({ id, positionBars: at, kind: 'cue', action: { module: 'opvisual', command: 'viz_macro', args } as RuleAction });
  };
  const step = 1 / (Math.max(1, sig) * 4);
  pts.forEach((pt, i) => {
    emit(pt.id, pt.position, pt.value);
    const next = pts[i + 1];
    if (!next || (pt.curve ?? 'linear') === 'hold' || next.position <= pt.position) return;
    let k = 1;
    for (let at = pt.position + step; at < next.position - 1e-9; at += step, k++) {
      emit(`${pt.id}:${k}`, at, pt.value + (next.value - pt.value) * shapeAt(pt.curve, (at - pt.position) / (next.position - pt.position)));
    }
  });
  return out;
}

/** An OP-MOTION lane as `/OP-Motion/*` OSC cues (2026-10-23, OPM4LBuilder's ask; the maths is `timeline/opMotion.ts`):
 *  one `seg` per segment, never samples (the device draws the curve on Live's beat), once per bus, to
 *  `127.0.0.1:11320` on the bridge that plays the timeline. That loopback only reaches OP-Motion on the computer
 *  running Live, which this app takes to be the primary (as every Ableton step does), so the lane pulls the timeline
 *  there (`timelinePlacement`) and, played anywhere else, is dropped and says where it would play. */
function motionCues(t: Timeline, lane: TimelineLane, cfg: BridgeConfig, dropped: DroppedStep[]): WireCue[] {
  const first = lane.steps.find((x) => x.kind === 'automationPoint');
  if (!first) return [];
  if (!motionTarget(lane.motion?.target)) {
    dropped.push({ kind: 'unresolvable', label: `OP-Motion (no target called "${lane.motion?.target ?? ''}")`, stepId: first.id });
    return [];
  }
  const live = primaryBridge();
  if (!live || live.id !== cfg.id) {
    dropped.push(live
      ? { kind: 'elsewhere', label: 'OP-Motion', machine: machineName(live), here: machineName(cfg), stepId: first.id }
      : { kind: 'unresolvable', label: 'OP-Motion (no computer paired)', stepId: first.id });
    return [];
  }
  const buses = motionBusesOf(lane);
  const out: WireCue[] = [];
  motionMessages(t, lane).forEach((m, i) => {
    for (const bus of buses) {
      out.push({
        id: `${m.stepId}:mo${i}:${bus}`, positionBars: m.at, kind: 'cue',
        action: abletonOscSend(m.kind === 'seg' ? MOTION_SEG_ADDRESS : MOTION_OFF_ADDRESS, motionOscArgs(lane, m, bus), MOTION_OSC_TARGET),
      });
    }
  });
  return out;
}

/** Whether `cfg` fires a timeline's `atStop` list when a run ends for any reason (OPBridge S339, `timeline_at_stop`,
 *  released v1.15.222; asked by us 2026-10-23). */
export function bridgeFiresAtStop(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['timeline_at_stop'] === true;
}

/** Whether `cfg` sends a timeline's `atStart` at the press of a start and a section's `atEnter` at the press of a jump to
 *  it (OPBridge S340, `timeline_at_start`, released v1.15.226; our yes to their shape 2026-10-27). */
export function bridgeSendsAtPress(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['timeline_at_start'] === true;
}

/** The OP-Motion lanes of `t` that play on `cfg`: on the computer running Live (the primary, as `motionCues`), heard,
 *  with a target the device knows and at least one point. */
function motionLanesOn(t: Timeline, cfg: BridgeConfig): TimelineLane[] {
  const live = primaryBridge();
  if (!live || live.id !== cfg.id) return [];
  return t.lanes.filter((lane) => lane.laneKind === 'automation' && !!lane.motion && laneAudible(t, lane)
    && !!motionTarget(lane.motion.target) && lane.steps.some((x) => x.kind === 'automationPoint'));
}

/** What the PRESS sends OP-Motion so a run that lands on `bar` glides in from wherever the effect is and arrives on the
 *  downbeat it lands on (`motionLandsAt`; answer 19 (a)). As the timeline's `atStart` (bar 0) and each section's
 *  `atEnter` (its start), only where `bridgeSendsAtPress`. At most 64. */
export function motionAtPress(t: Timeline, cfg: BridgeConfig, bar: number): RuleAction[] {
  return motionLandsAt(motionLanesOn(t, cfg), bar).map((m) => abletonOscSend(m.address, m.args, MOTION_OSC_TARGET)).slice(0, 64);
}

/** Bar 0's press list, or none when bar 0 is the lead-in: a run starts there, and nothing is sent in a lead-in (the
 *  first section's own cues glide it in, as before). */
function atStartOf(t: Timeline, cfg: BridgeConfig): RuleAction[] {
  const first = timelinePlaySections(t)[0];
  if (!first || first.id === leadInSectionId(t)) return [];
  return motionAtPress(t, cfg, 0);
}

/** Each played section's start bar, by id (the lead-in has none: a jump never lands there). */
function sectionStartBars(t: Timeline): Map<string, number> {
  const out = new Map<string, number>();
  let at = 0;
  for (const sec of timelinePlaySections(t)) {
    if (sec.id !== leadInSectionId(t)) out.set(sec.id, at);
    at += Math.max(0, sec.lengthBars);
  }
  return out;
}

/** Whether `cfg` blends a section's (or a start's) lights in from the press (OPBridge S351, `timeline_section_arrival`,
 *  asked by us 2026-10-31). An older bridge cuts on the line whatever is sent. */
export function bridgeBlendsArrival(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['timeline_section_arrival'] === true;
}

/** A light's brightness for the slam rule: its brightest channel, white included. */
const lightLevel = (c: { r: number; g: number; b: number; w?: number } | null): number => (c ? Math.max(c.r, c.g, c.b, c.w ?? 0) : 0);

/** What every lights lane shows at a moment, as it PLAYS (the Stream view's maths, `colourStream.ts`): each single light
 *  with the masters that reach it, and each "All lights" master as its own colour where it takes over. */
function lightSamplers(t: Timeline): Array<{ laneId: string; at: (pos: number) => { r: number; g: number; b: number; w?: number } | null }> {
  const total = timelineTotalBars(t);
  const dark = timelineStartsDark(t);
  const played = (l: TimelineLane): TimelineLane => ({ ...l, steps: dark && l.laneKind === 'dmx-fixture' ? darkStartSteps(l.id, playedSteps(t, l)) : playedSteps(t, l) });
  const masterLanes = t.lanes.filter((l) => l.laneKind === 'dmx-rig');
  const tks = new Map(masterLanes.map((m) => [m.id, laneAudible(t, m) ? takeoversOf(played(m), total) : []]));
  const out: Array<{ laneId: string; at: (pos: number) => { r: number; g: number; b: number; w?: number } | null }> = [];
  for (const m of masterLanes) {
    const list = tks.get(m.id)!;
    out.push({ laneId: m.id, at: (pos) => {
      for (const tk of list) { const w = takeoverWeight(tk, pos); if (w > 0) { const c = takeoverColour(tk, pos); return { r: c.r * w, g: c.g * w, b: c.b * w, w: (c.w ?? 0) * w }; } }
      return null;
    } });
  }
  for (const lane of t.lanes.filter((l) => l.laneKind === 'dmx-fixture')) {
    const keptOut = fixtures().find((f) => f.id === lane.fixtureRef?.fixtureId)?.passthrough?.['keepOutOfMasters'] === true;
    const ms = mastersReaching(masterLanes, lane, keptOut).map((m) => ({ takeovers: tks.get(m.id) ?? [], audible: laneAudible(t, m) }));
    const own = { at: ownColourOf(played(lane)), audible: laneAudible(t, lane) };
    out.push({ laneId: lane.id, at: (pos) => lightColourWith(own, ms, pos)?.colour ?? null });
  }
  return out;
}

/** The lights a SLAM lands on, when section `sectionId` opens with one (2026-11-04, operator: a section that opens with
 *  a slam refuses Blend, "say why"): on its first beat at least two lights land at 0.8 of full or more, drop by 0.25 or
 *  more within the beat, and do not come back up to near that peak in the rest of the bar. That is the lights kit's
 *  `slam` (`show.ts`: lands at full, settles in a beat); the last clause keeps out a kick that hits on every beat, which
 *  lands and settles the same way but comes back. A blend would turn the landing into a swell, so such a section always
 *  cuts. `[]` = no slam. Played positions (the lead-in included); the lead-in itself never slams. */
export function sectionSlam(t: Timeline, sectionId: string): string[] {
  const start = sectionStartBars(t).get(sectionId);
  if (start === undefined) return [];
  const bar = 1; const beat = 0.25;
  const hits: string[] = [];
  for (const s of lightSamplers(t)) {
    const peak = lightLevel(s.at(start));
    if (peak < 0.8 || peak - lightLevel(s.at(start + beat)) < 0.25) continue;
    let back = false;
    for (let p = start + beat; p < start + bar - 1e-9 && !back; p += 1 / 64) back = lightLevel(s.at(p)) >= peak - 0.1;
    if (!back) hits.push(s.laneId);
  }
  return hits.length >= 2 ? hits : [];
}

/** The arrival a section is SENT with: `blend` only where the bridge plays it and the section does not open with a slam. */
function sectionArrivalWire(t: Timeline, sectionId: string, arrival: TimelineSection['arrival'], blends: boolean): { arrival?: 'blend' } {
  return blends && arrival === 'blend' && sectionSlam(t, sectionId).length === 0 ? { arrival: 'blend' } : {};
}

/** The timeline-level arrival sent: `blend` only where the bridge plays it and the first section does not slam. */
function timelineArrivalWire(t: Timeline, blends: boolean): { arrival?: 'blend' } {
  const first = timelinePlaySections(t).find((s) => s.id !== leadInSectionId(t));
  return blends && t.arrival === 'blend' && !(first && sectionSlam(t, first.id).length > 0) ? { arrival: 'blend' } : {};
}

/** What a run of `t` sends to OP-Motion when it ENDS, however it ends (a stop, stop-all, panic, another Song replacing
 *  it): `/OP-Motion/off <bus> <target> <tail_beats> <shape>` for every OP-Motion lane that plays on `cfg`, per bus, over
 *  the lane's own glide-out. Without it an effect stopped part-way kept going until OP-Motion's lease lapsed. A natural
 *  end also sends the lane's timed `off` cue; off then off is harmless (our ask, accepted by OPBridge). At most 64. */
export function motionAtStop(t: Timeline, cfg: BridgeConfig): RuleAction[] {
  const live = primaryBridge();
  if (!live || live.id !== cfg.id) return [];
  const out: RuleAction[] = [];
  for (const lane of t.lanes) {
    if (lane.laneKind !== 'automation' || !lane.motion || !laneAudible(t, lane)) continue;
    if (!motionTarget(lane.motion.target) || !lane.steps.some((x) => x.kind === 'automationPoint')) continue;
    const tail = { none: 0, beat: 1, bar: OPBRIDGE_BEATS_PER_BAR, '2bars': 2 * OPBRIDGE_BEATS_PER_BAR }[motionGlideOut(lane)];
    for (const bus of motionBusesOf(lane)) out.push(abletonOscSend(MOTION_OFF_ADDRESS, [bus, lane.motion.target, tail, SHAPE_INT.s], MOTION_OSC_TARGET));
  }
  return out.slice(0, 64);
}

interface WireCue {
  id: string;
  positionBars: number;
  kind: 'cue';
  action: RuleAction;
}

/** The `pwa` command a timeline's light cue carries (DEC-269). OPBridge
 *  pushes it back as `pwa_action { command, args }`; `timeline/canvasLights.ts`
 *  listens for exactly this name. */
export const TIMELINE_LIGHT_COMMAND = 'timeline_light';

/** What the canvas does with a light cue: a short flash, or held on until
 *  the matching 'off'. */
export type TimelineLightMode = 'flash' | 'on' | 'off';

/** The args a light cue carries, and what the canvas reads back. */
export interface TimelineLightArgs {
  itemId: string;
  mode: TimelineLightMode;
  /** The authored step, for diagnostics; the canvas keys on `itemId`. */
  stepId: string;
}

function lightCue(id: string, positionBars: number, itemId: string, mode: TimelineLightMode, stepId: string): WireCue {
  const args: TimelineLightArgs = { itemId, mode, stepId };
  return { id, positionBars, kind: 'cue', action: { module: 'pwa', command: TIMELINE_LIGHT_COMMAND, args: { ...args } } };
}

/** A lights-lane step sent as we hold it (OPBridge S300): a colour keyframe
 *  or a Hand back, every field kept, `position` renamed to the wire's
 *  `positionBars`. */
interface WireColourStep {
  id: string;
  positionBars: number;
  kind: 'colorMarker' | 'lightRelease';
  [field: string]: unknown;
}

/** A Light FX block as OPBridge plays it (S295, `timeline_lighting`). */
interface WireAnimationStep {
  id: string;
  positionBars: number;
  kind: 'animation';
  durationBars: number;
  preset: string;
  params?: Record<string, unknown>;
}

/** A drum lights route as OPBridge reads it (S331, `StepKind::HitRoute`). */
interface WireHitRouteStep {
  id: string;
  positionBars: number;
  kind: 'hitRoute';
  source: Record<string, unknown>;
  lookId: string;
}

/** A designed drum hit as OPBridge S339 reads it: `{ kind: "hit", positionBars, source: {role} | {track}, velocity }`. */
interface WireHitStep {
  id: string;
  positionBars: number;
  kind: 'hit';
  source: { role: string } | { track: string };
  velocity: number;
}

type WireStep = WireCue | WireColourStep | WireAnimationStep | WireHitRouteStep | WireHitStep;

/** Why a step produced nothing on the bridge being pushed to (or, for
 *  `missing-port`, why it will not sound there). Every entry becomes a
 *  sentence in `droppedWarning` - see {@link summarizeDropped}. Nothing a
 *  timeline cannot play is dropped silently: the operator's Helix steps
 *  vanished that way for weeks (2026-09-29). `stepId` is set by
 *  {@link toBridgeTimeline} so the editor can mark the step itself (plan 3.3). */
type DroppedStep = { stepId?: string } & (
  /** A kind a timeline cannot fire yet (Stream Deck, OBS Scene, ...). */
  | { kind: 'unsupported'; label: string }
  /** Tied to another machine than the one this timeline plays on. */
  | { kind: 'elsewhere'; label: string; machine: string; here: string }
  /** Tied to a machine this device has no pairing for, or a removed pedal. */
  | { kind: 'unpaired'; label: string }
  /** No MIDI port to send on: the pedal has none, or the bridge has no default output. */
  | { kind: 'no-port'; label: string; machine: string }
  /** WARNING ONLY - the cue is still pushed: its port is not on that machine. */
  | { kind: 'missing-port'; label: string; port: string; machine: string }
  /** The step points at a canvas item that no longer exists. */
  | { kind: 'missing-item' }
  /** That machine's OPBridge cannot play it as drawn yet (a light group's
   *  master on a bridge without `timeline_light_groups`). */
  | { kind: 'needs-update'; label: string; machine: string }
  /** A drum lights look with no light to light on that machine (none picked, or "every light" on an empty rig): sent
   *  as a route to nothing, so it spends none of OPBridge's flash limit. */
  | { kind: 'no-lights'; label: string; machine: string }
  /** Resolved to nothing for a reason not named above. */
  | { kind: 'unresolvable'; label: string });

/** How one timeline compiles for one bridge. */
export interface CompileOpts {
  /** Steps with no machine of their own are included on THIS bridge. True
   *  for the timeline's home; a launcher fanning out to several bridges sets
   *  it on exactly one of them, so a plain note does not sound twice. */
  followsHere?: boolean;
  /** That bridge's port names, from {@link knownPortsFor}. `null` skips the
   *  port check (never read yet); it never blocks a push. */
  knownPorts?: ReadonlySet<string> | null;
  /** Row ids of the OTHER bridges the same launch goes to. A step tied to one
   *  of them is carried there, so it is skipped here without a warning; only
   *  a step no target of the launch can play is named. Empty for the usual
   *  single-bridge launch. */
  siblings?: ReadonlySet<string>;
  /** Toggle steps SET OPBridge's toggle (DEC-269 Phase B). Defaults to the
   *  bridge's `toggle_set` capability; a smoke passes it to compile offline. */
  toggleSet?: boolean;
  /** The bridge reports fired steps, so no `pwa` light cues are sent.
   *  Defaults to its `timeline_fired` capability. */
  timelineFired?: boolean;
  /** The bridge blends a section's lights in from the press (S351). Defaults to its `timeline_section_arrival`
   *  capability; a smoke passes it to compile offline. */
  sectionArrival?: boolean;
}

/** A button the toggle ENGINE plays: toggle mode (the default) and a CC or
 *  note action, exactly the buttons `FireButton.tsx::toggleConfig` pushes to
 *  `/toggles` and flips there. Its toggle id is the item id. */
export function isEngineToggle(item: LayoutItem): boolean {
  if (item.kind !== 'button') return false;
  if ((item.mode ?? 'toggle') !== 'toggle') return false;
  const a = item.actionType ?? 'cc';
  return a === 'cc' || a === 'noteon';
}

/** Does a timeline step on this item hold its light from its bar to its
 *  right edge (an authored length, or a note that needs its note-off)?
 *  Otherwise it flashes. One rule for the compile's light cues and for the
 *  canvas reading `timeline_fired` frames, so the two cannot disagree. */
export function stepHoldsLight(item: LayoutItem, step: { durationBars?: number | undefined }): boolean {
  if (item.kind !== 'button') return false;
  const authored = step.durationBars;
  if (typeof authored === 'number' && Number.isFinite(authored) && authored > 0) return true;
  return (item.actionType ?? 'cc') === 'noteon';
}

function toggleSetAction(toggleId: string, state: boolean): RuleAction {
  return { module: 'toggle', command: 'set', args: { toggleId, state } };
}

/** OPVisual's named cell and column actions (DEC-278). */
export const OPV_ACTION_ID = /^opvisual-(cell-l\d+-\d+|col-\d+)$/;

/**
 * A timeline step that fires an OPVisual cell or column does NOT follow by
 * default (2026-10-18; OPVisual DEC-148, their operator: "Step decides, ignore
 * by default" - the timeline is the script). The action's `follow` input is
 * written as 0 unless the step itself says `values.follow: 1` (its Follow
 * switch). Old steps without `values` therefore compile to follow off too.
 */
export function withOpvFollowDefault(a: RuleAction): RuleAction {
  const args = a.args as { id?: unknown; values?: Record<string, unknown> } | undefined;
  if (a.module !== 'action' || a.command !== 'fire' || typeof args?.id !== 'string' || !OPV_ACTION_ID.test(args.id)) return a;
  if (args.values && typeof args.values['follow'] === 'number') return a;
  return { ...a, args: { ...args, values: { ...(args.values ?? {}), follow: 0 } } } as RuleAction;
}

function resolveStepToCues(
  step: TimelineStep, cfg: BridgeConfig, timelineId: string, dropped: DroppedStep[], sigNum: number,
  opts: Required<CompileOpts>,
): WireStep[] {
  if ((isColorMarkerStep(step) || isLightReleaseStep(step)) && bridgePlaysColour(cfg)) {
    const { position, ...rest } = step;
    // A bridge without white LEDs folds `w` into RGB as it always has; `wTemp` means nothing to it, so it is not sent.
    if (isColorMarkerStep(step) && step.color.wTemp !== undefined && !bridgePlaysWhites(cfg)) {
      const { wTemp: _t, ...color } = step.color;
      void _t;
      return [{ ...rest, color, positionBars: position }];
    }
    return [{ ...rest, positionBars: position }];
  }
  // A Light FX block (OPBridge S295): the preset plays for `durationBars` from its beat, on the lane's light (a
  // single-light lane) or the whole rig (any other lane), on the bridge that owns those lights.
  if (step.kind === 'animation' && bridgePlaysLightFx(cfg)) {
    return [{
      id: step.id, positionBars: step.position, kind: 'animation', durationBars: Math.max(1 / 16, step.durationBars || 1), preset: step.preset,
      ...(step.params && Object.keys(step.params).length ? { params: step.params } : {}),
    }];
  }
  if (isCueStep(step)) {
    // No authoring UI produces a raw `cue` step today, but the schema
    // allows one and a future surface might - pass an already-shaped
    // action through rather than silently drop authored intent.
    const a = step.action as Partial<RuleAction> | undefined;
    if (a && typeof a.module === 'string' && typeof a.command === 'string' && a.args && typeof a.args === 'object') {
      // The ONE step kind that crosses 1:1, so the ONE that can carry a
      // step-level passthrough (schema's `unknownFieldsOf` block comment
      // explains why the compiled kinds cannot). Extras spread FIRST so a
      // foreign key colliding with a wire-only name (`positionBars`) loses
      // to ours rather than corrupting the payload.
      const extras = unknownFieldsOf(step, TIMELINE_CUE_STEP_KNOWN_KEYS);
      return [{ ...extras, id: step.id, positionBars: step.position, kind: 'cue', action: withOpvFollowDefault(a as RuleAction) }];
    }
    return [];
  }
  if (!isButtonRefStep(step)) {
    const label = unplayableKindLabel(step);
    if (label) dropped.push({ kind: 'unsupported', label });
    return [];
  }
  const item = findLayoutItemById(step.buttonId);
  if (!item || !isFireableItem(item)) {
    dropped.push({ kind: 'missing-item' });
    return [];
  }
  if (item.kind === 'streamdeck') {
    // Authorable via the palette (DEC-172) but confirmed not reachable for
    // firing (module doc comment above) - a real gap, not a config error,
    // so it earns an operator-facing warning rather than console-only.
    dropped.push({ kind: 'unsupported', label: 'Stream Deck' });
    return [];
  }
  if (item.kind === 'button' && item.actionType === 'obs') {
    // An OBS Scene button cannot become a Cue, and the way it FAILED to
    // before this guard was the dangerous kind: `resolveFireableItemActions`
    // handles `ableton` explicitly and then falls through to the CC branch
    // for everything else, so a timeline referencing an OBS button pushed
    // `[0xB0|ch, ccNumber, onValue]` on the bridge's default MIDI port -
    // cc 0 at Ableton, from the placeholder `ccNumber: 0` the item carries
    // precisely because nothing reads it. Not a no-op: a WRONG SEND, at a
    // gig, from a step that looks like a scene change on the timeline.
    //
    // It cannot be made to work here either. A Cue is an opaque
    // `{module, command, args}` executed by OPBridge's rules engine, and
    // that engine's `obs` module is a CONNECTOR - `rules_wiring.rs` publishes
    // an `engine.action` for the separate `opbridge-stream` binary, which by
    // OPBridge's own note has no launch story and is not in `ArtefactSet`.
    // So `module:'obs'` would be accepted and then dropped on any machine
    // where nobody started that binary by hand, which is every machine.
    //
    // Same treatment as Stream Deck above: an operator-facing warning at
    // push time, because a step the operator drew and that will never fire
    // must not be silent. See DEC-205; the unblock is in PLAN.md QUEUED.
    dropped.push({ kind: 'unsupported', label: 'OBS Scene' });
    return [];
  }
  if (item.kind === 'button' && item.actionType === 'runProgram') {
    // A Run Program button cannot become a Cue, and without this guard it
    // would fall through to the CC branch below and push `[0xB0|ch, 0, 127]`
    // from the placeholder `ccNumber: 0` - the exact wrong send the OBS guard
    // above was written after. There is no Cue shape for it either: a launch
    // is `POST /program/launch/{id}` on a NAMED bridge, and the rules engine
    // has no program-launch module to express it. Operator-facing warning at
    // push time, same as OBS.
    dropped.push({ kind: 'unsupported', label: 'Run Program' });
    return [];
  }
  if (item.kind === 'button' && item.actionType === 'lightPreset') {
    // Same trap and same answer as Run Program: without this guard a Light
    // Preset button falls through to the CC branch and pushes cc 0 from its
    // placeholder `ccNumber`. A preset is `POST /lighting/fire` on a NAMED
    // bridge, which a Cue cannot express. Warned at push time.
    dropped.push({ kind: 'unsupported', label: 'Light Preset' });
    return [];
  }
  if (item.kind === 'button' && item.actionType === 'abletonDevice') {
    // An Ableton Device button cannot become a Cue YET, and it is dropped
    // here for the same reason the OBS guard above exists: without it,
    // `resolveFireableItemActions` falls through to the CC branch and pushes
    // `[0xB0|ch, ccNumber, onValue]` on the bridge's default MIDI port from
    // the placeholder `ccNumber: 0` the item carries because nothing reads
    // it. cc 0 at Ableton, from a step the operator drew as a device toggle.
    //
    // ⚠️ [CORRECTED 2026-09-20: this comment said the blocker was that
    // `OP-ButtonsDevice` "does not exist yet". It was BUILT 2026-09-19
    // (`v0.1-rc2`, loaded in Live, heartbeat reaching OPBridge) and the
    // manual fire path now sends to it. The guard stays, but for a DIFFERENT
    // reason, and naming the wrong blocker is how a guard outlives its
    // cause.]
    //
    // The remaining blocker is the CUE shape, not the device. A Cue is an
    // opaque `{module, command, args}` executed by OPBridge's rules engine,
    // and the manual path reaches this device over `POST /osc/send` to
    // `127.0.0.1:11280` - a per-item, per-bridge HTTP call. Two things are
    // missing before a Cue can carry it: a rules-engine action that emits
    // OSC to a loopback target, and the per-item BRIDGE AFFINITY that action
    // would need, because `127.0.0.1` resolves on whatever machine the rule
    // happens to run on. That second half is the sharp one - without it a
    // misrouted cue is accepted, answered `{ok:true}` and dropped by UDP
    // with no error anywhere, which is strictly worse than this drop.
    //
    // Warned rather than silent, per this file's contract: a step that will
    // never fire must say so at push time. See `.notepads/
    // ableton-device-control-plan.md`.
    dropped.push({ kind: 'unsupported', label: 'Ableton Device' });
    return [];
  }
  // Placement, decided once here (2026-09-29): a step tied to another
  // machine is named, not silently skipped; a step tied to no machine plays
  // wherever this timeline plays (the caller says whether that is here).
  const label = stepItemLabel(item);
  const placement = itemPlacement(item);
  if (placement.kind === 'unresolved') {
    dropped.push({ kind: 'unpaired', label });
    return [];
  }
  if (placement.kind === 'bound' && !placement.bridges.some((b) => b.id === cfg.id)) {
    if (placement.bridges.some((b) => opts.siblings.has(b.id))) return []; // another target of this launch plays it
    dropped.push({ kind: 'elsewhere', label, machine: machineName(placement.bridges[0]), here: machineName(cfg) });
    return [];
  }
  if (placement.kind === 'follows' && !opts.followsHere) return [];
  const resolved = resolveFireableItemActions(item, cfg, { skipBridgeCheck: true, beatsPerBar: OPBRIDGE_BEATS_PER_BAR });
  if (!resolved || resolved.on.length === 0) {
    const pedal = item.pedalId ? scopedPedals().find((pd) => pd.id === item.pedalId) : undefined;
    if (pedal && !pedal.port) {
      dropped.push({ kind: 'no-port', label, machine: machineName(placement.kind === 'bound' ? placement.bridges[0] : cfg) });
    } else if (!pedal && item.kind === 'button' && item.actionType !== 'ableton' && item.actionType !== 'hueScene' && !cfg.defaultMidiOutput) {
      dropped.push({ kind: 'no-port', label, machine: machineName(cfg) });
    } else {
      dropped.push({ kind: 'unresolvable', label });
    }
    return [];
  }
  // Multiple actions (a compound PresetChange) share the step's bar
  // position and fire in array order - see the module doc comment's
  // "stable sort" note for why that's safe.
  //
  // DEC-269 Phase B (OPBridge S314): a toggle button's step SETS OPBridge's
  // toggle instead of sending its raw MIDI. The raw MIDI went past the toggle
  // engine, so after a timeline turned a toggle on, `/toggles` still said off,
  // every canvas showed off, and the next tap re-sent "on". `set` is
  // idempotent and fires the toggle's own on/off action, the same bytes a tap
  // sends. Same placement and port rules as the raw path above (it resolved),
  // so a toggle no machine can play is still named, not silently sent.
  const engineToggle = opts.toggleSet && isEngineToggle(item);
  const cues: WireCue[] = engineToggle
    ? [{ id: step.id, positionBars: step.position, kind: 'cue' as const, action: toggleSetAction(item.id, true) }]
    : resolved.on.map((action, i) => ({
      id: i === 0 ? step.id : `${step.id}-${i}`,
      positionBars: step.position,
      kind: 'cue' as const,
      action,
    }));

  // The authored block LENGTH, finally reaching the wire (2026-08-23).
  //
  // `ButtonRefStep.durationBars` has been real since the block gained an
  // edge-resize handle - authored in `TimelineEditor.tsx`, persisted with a
  // 1/16-bar clamp by `timeline/store.ts::setStepDuration`, and promised by
  // its own schema comment ("holds the button's ON state for `durationBars`
  // and then fires OFF"). It was never translated here, so every note block
  // pushed a bare note-on and the note sustained forever. That is the
  // operator's "notes should stop at the end of the block" report, and
  // OPBridge's ❓ in `OPApps/opbridge-opbuttons.md` asking whose fix it was:
  // theirs only if the length were purely visual, ours because it is real.
  //
  // A Cue has no duration concept server-side (a Cue is opaque
  // `{module, command, args}`), so a release is simply a SECOND cue at
  // `position + durationBars`. No new step kind, no OPBridge change.
  //
  // **The release lands at the block's VISIBLE RIGHT EDGE** - operator's
  // own spec, 2026-08-23: "i want the note off to be the far right of the
  // block". That is one rule covering both cases, which is why there is a
  // fallback here rather than an operator decision about default length:
  //
  //   - resized block  -> right edge is `position + durationBars`
  //   - un-resized     -> `TimelineEditor.tsx`'s `width()` draws it exactly
  //                       ONE BEAT wide, so its right edge is one beat.
  //
  // What you see is where the note stops, always. The first draft honoured
  // only an explicit `durationBars` and so did nothing at all for a real
  // operator timeline (every step un-resized, five bare note-ons, notes
  // sustaining forever - confirmed by reading the pushed library off the
  // live bridge). An un-resized note-on with no release is never correct:
  // it can only hang. One beat is short, but it is honest to the drawing
  // and infinitely better than never releasing.
  //
  // Kinds with no meaningful release (cc, pc, presetChange, snapshotChange)
  // carry an empty `off` and are untouched by any of this.
  //
  // Deliberately NOT clamped to the timeline's end. A block drawn past the
  // last bar puts its release beyond the runtime's reach - but clamping
  // here would be inventing playback policy in a translation layer, and
  // OPBridge already covers exactly this: their `held_notes` ledger drains
  // synthetic note-offs when a runtime is abandoned, including at a
  // non-looping end. Their net catches what the authored release cannot.
  const authored = step.durationBars;
  const dur = typeof authored === 'number' && Number.isFinite(authored) && authored > 0
    ? authored
    : 1 / sigNum; // one beat - the un-resized block's drawn width
  if (engineToggle) {
    // A held block turns the toggle back off at its right edge; a bare step
    // leaves it on ("on for a toggle", operator 2026-10-11).
    if (stepHoldsLight(item, step)) {
      cues.push({ id: `${step.id}-off`, positionBars: step.position + dur, kind: 'cue' as const, action: toggleSetAction(item.id, false) });
    }
  } else if (resolved.off.length > 0) {
    const offAt = step.position + dur;
    for (const [i, action] of resolved.off.entries()) {
      cues.push({
        id: i === 0 ? `${step.id}-off` : `${step.id}-off-${i}`,
        positionBars: offAt,
        kind: 'cue' as const,
        action,
      });
    }
  }

  // The timeline lights the canvas (DEC-269, 2026-10-11). One extra cue per
  // resolved step: a `pwa` action that OPBridge pushes to every device of
  // this user as a `pwa_action` frame, which `timeline/canvasLights.ts` turns
  // into the item lighting up. Appended last, so after the stable sort by
  // position it fires AFTER the MIDI at the same bar. OPBridge confirmed
  // from source (opbridge-96, 2026-10-11): any command name passes, `args`
  // arrive untouched, and the extra action cannot delay or fail the MIDI.
  // Item id, mode and step id travel in `args` because the PWA's frame parse
  // keeps only `command` and `args`. Never put `bytes` or `port` in them:
  // smokes find MIDI by scanning for those keys.
  //
  // A button with a length (an authored block, or a note that needs its
  // note-off) is HELD: 'on' at its bar, 'off' at the block's right edge, the
  // same instant its note-off goes. Everything else - a CC/PC/Ableton button
  // with no length, a preset or snapshot change - FLASHES.
  //
  // Phase B: a bridge that reports fired steps (`timeline_fired`) gets no
  // light cues. `timeline/canvasLights.ts` lights the item from OPBridge's
  // frame for the step itself, and only when its send succeeded.
  if (!opts.timelineFired) {
    const held = stepHoldsLight(item, step);
    cues.push(lightCue(`${step.id}-light`, step.position, item.id, held ? 'on' : 'flash', step.id));
    if (held) cues.push(lightCue(`${step.id}-off-light`, step.position + dur, item.id, 'off', step.id));
  }

  // The port check (2026-09-29). WARNS, never removes: see the module doc's
  // placement section for why a remove-on-miss rule would be dangerous.
  if (opts.knownPorts) {
    const missing = new Set<string>();
    for (const cue of cues) {
      const port = midiPortOf(cue.action);
      // Built-ins are present whatever the list says (macOS virtual sources) -
      // checked HERE, not only where the list is built, so no caller can
      // reintroduce the false alarm by passing its own list.
      if (port !== null && !opts.knownPorts.has(port) && !OPBRIDGE_BUILTIN_PORTS.has(port)) missing.add(port);
    }
    for (const port of missing) dropped.push({ kind: 'missing-port', label, port, machine: machineName(cfg) });
  }

  return cues;
}

/** Collapse what {@link resolveStepToCues} recorded into one operator-facing
 *  sentence fragment, e.g. `"2 Stream Deck steps can't fire from a Timeline
 *  yet; 1 Guitar FX Preset step is on Mac Studio, not Gaming-PC"`. `null`
 *  when nothing was recorded. The unsupported-kind wording is unchanged from
 *  2026-08-21 (smokes read it). */
function summarizeDropped(dropped: DroppedStep[]): string | null {
  if (dropped.length === 0) return null;
  const plural = (n: number): string => (n === 1 ? '' : 's');
  const tally = (keys: string[]): Map<string, number> => {
    const counts = new Map<string, number>();
    for (const key of keys) counts.set(key, (counts.get(key) ?? 0) + 1);
    return counts;
  };
  const SEP = '\u0000';
  const parts: string[] = [];

  const unsupported = tally(dropped.flatMap((d) => (d.kind === 'unsupported' ? [d.label] : [])));
  if (unsupported.size > 0) {
    const listed = [...unsupported].map(([label, n]) => `${n} ${label} step${plural(n)}`);
    parts.push(`${listed.join(', ')} can't fire from a Timeline yet`);
  }
  for (const [key, n] of tally(dropped.flatMap((d) => (d.kind === 'elsewhere' ? [[d.label, d.machine, d.here].join(SEP)] : [])))) {
    const [label, machine, here] = key.split(SEP);
    parts.push(`${n} ${label} step${plural(n)} ${n === 1 ? 'is' : 'are'} on ${machine}, not ${here}`);
  }
  for (const [label, n] of tally(dropped.flatMap((d) => (d.kind === 'unpaired' ? [d.label] : [])))) {
    parts.push(`${n} ${label} step${plural(n)} ${n === 1 ? 'drives' : 'drive'} a device on a computer this one is not paired with`);
  }
  for (const [key, n] of tally(dropped.flatMap((d) => (d.kind === 'no-port' ? [[d.label, d.machine].join(SEP)] : [])))) {
    const [label, machine] = key.split(SEP);
    parts.push(`${n} ${label} step${plural(n)} ${n === 1 ? 'has' : 'have'} no MIDI output set on ${machine}`);
  }
  for (const [key, n] of tally(dropped.flatMap((d) => (d.kind === 'missing-port' ? [[d.port, d.machine].join(SEP)] : [])))) {
    const [port, machine] = key.split(SEP);
    parts.push(`MIDI port "${port}" isn't on ${machine}, so ${n} step${plural(n)} won't sound`);
  }
  for (const [key, n] of tally(dropped.flatMap((d) => (d.kind === 'needs-update' ? [[d.label, d.machine].join(SEP)] : [])))) {
    const [label, machine] = key.split(SEP);
    parts.push(`${n} ${label} step${plural(n)} wait${n === 1 ? 's' : ''} for OPBridge on ${machine} to update`);
  }
  const missingItems = dropped.filter((d) => d.kind === 'missing-item').length;
  if (missingItems > 0) {
    parts.push(`${missingItems} step${plural(missingItems)} ${missingItems === 1 ? 'points' : 'point'} at a control that no longer exists`);
  }
  for (const [key, n] of tally(dropped.flatMap((d) => (d.kind === 'no-lights' ? [[d.label, d.machine].join(SEP)] : [])))) {
    const [label, machine] = key.split(SEP);
    parts.push(`${n} ${label} block${plural(n)} ${n === 1 ? 'has' : 'have'} no lights to light on ${machine}`);
  }
  for (const [label, n] of tally(dropped.flatMap((d) => (d.kind === 'unresolvable' ? [d.label] : [])))) {
    parts.push(`${n} ${label} step${plural(n)} couldn't be resolved`);
  }
  return parts.join('; ');
}

export interface WireTimelineResult {
  wire: Record<string, unknown> | null;
  /** Set when one or more steps resolved to zero cues for a known,
   *  nameable reason (currently: Stream Deck) rather than a config error —
   *  `null` when nothing was dropped this way. Present regardless of
   *  whether `wire` itself is null (a timeline whose ONLY steps are
   *  Stream Deck ones still has something to tell the operator). */
  droppedWarning: string | null;
  /** The same, per step: step id -> why it will not play (plan 3.3). Empty
   *  when every step plays. */
  stepWarnings: Map<string, string>;
}

/** Translate one PWA `Timeline` into OPBridge's wire shape for firing on
 *  `cfg` specifically. Because buttonRef resolution is bridge-relative
 *  (a pedal-bound tile only resolves on the bridge it's actually wired
 *  to), pushing the SAME timeline to two different mesh bridges can
 *  legitimately produce two different payloads - each is correct for its
 *  own machine. `wire` is `null` when there's nothing to push: an
 *  unsupported tier (`genre` - M5 not built), or zero steps resolved to a
 *  Cue on this bridge. */
export function toBridgeTimeline(t: Timeline, cfg: BridgeConfig, opts: CompileOpts = {}): WireTimelineResult {
  return inProjectOf(t, () => {
  if (t.tier !== 'action' && t.tier !== 'song') return { wire: null, droppedWarning: null, stepWarnings: new Map() };
  if (isChain(t)) return chainWire(t, cfg);
  const dropped: DroppedStep[] = [];
  const compile: Required<CompileOpts> = {
    followsHere: opts.followsHere ?? true,
    knownPorts: opts.knownPorts ?? null,
    siblings: opts.siblings ?? new Set<string>(),
    toggleSet: opts.toggleSet ?? bridgeSetsToggles(cfg),
    timelineFired: opts.timelineFired ?? bridgeReportsFired(cfg),
    sectionArrival: opts.sectionArrival ?? bridgeBlendsArrival(cfg),
  };
  // Unknown-field passthrough, per the envelope we committed to OPBridge on
  // 2026-09-14 (`OPApps/opbridge-opbuttons.md` answer (b)). Every level here
  // used to be a bare object literal, which is where - and measurably the
  // ONLY place - a field our schema doesn't name died on our side. See the
  // schema's `unknownFieldsOf` block comment for what "unknown" means (absent
  // from OUR schema) and why the maps are keyed types.
  //
  // `[CORRECTED 2026-09-17]` This said `laneKind`/`fixtureRef` "stay off the
  // wire until the DMX content lands separately". They no longer do - the lane
  // map below emits both. They were never "unknown" in the passthrough sense
  // anyway: both are in `TIMELINE_LANE_KNOWN_KEYS`, so the passthrough
  // deliberately excluded them and only an explicit emit could ever have sent
  // them.
  //
  // Extras are spread FIRST at every level so a foreign key that happens to
  // collide with a wire-only name (`positionBars`, `loops`) loses to ours
  // instead of corrupting the payload. `unknownFieldsOf` returns null when
  // there is nothing foreign, so an ordinary document still produces a
  // byte-identical payload and no spurious re-push.
  //
  // Safe against OPBridge today: none of `Timeline` / `Section` / `Lane` /
  // `Step` in `crates/opbridge-timelines/src/lib.rs` carries
  // `#[serde(deny_unknown_fields)]`, so an unknown key is ignored, not
  // rejected. Read at their HEAD 2026-09-17, not assumed.
  // Light groups (DEC-251): a bridge that does not scope masters to their
  // group would play each master over every light, so when this timeline
  // separates any light it gets no master steps at all; the lights' own
  // lanes still play. With no groups nothing changes.
  const holdMasters = bridgePlaysColour(cfg) && !bridgePlaysLightGroups(cfg)
    && t.lanes.some((l) => isLightsLane(l) && lightGroupOf(l) !== '');
  // A DARK START (`lightsAtStart`, 2026-10-20): every light black from bar 0 until its lane colours it. OPBridge
  // leaves a light alone before its lane's first keyframe and never touches a light with no lane (colour_lanes.rs
  // `own_colour`, read 2026-10-20), so the black is compiled in: a held OFF key on bar 0 of each light's lane, and a
  // lane holding OFF for each light of the rig that has none. Timeline colour sits below every press and pad on
  // OPBridge, so a held black never blocks a live control, and a master still takes these lights over. Only for a
  // timeline that plays lights at all, and only to a bridge that plays colour.
  // DRUM LIGHTS (2026-10-23, the `instrument` lane, wire agreed with OPBridge S331): its routes and the timeline's looks
  // go only to a bridge that routes live hits (`timeline_instrument_routing`); an older one gets the lane with no steps
  // and each step says it waits for that bridge's update, as a zone lane does. A drum lane on such a bridge counts as
  // lights for the dark start and end: its hits fall back into the dark it leaves.
  const drums = bridgePlaysInstruments(cfg);
  const lightLike = (l: TimelineLane) => isLightsLane(l) || (drums && isInstrumentLane(l) && laneAudible(t, l) && l.steps.length > 0);
  // A block's two looks, written out for THIS bridge's rig ("every light" = the lights it drives). Only looks a sent
  // route names go on the wire (OPBridge drops every look when one fails to parse, so nothing else rides along).
  const blockLooks = new Map<string, InstrumentLook>();
  if (drums) {
    const rig = rigOn(cfg);
    for (const lane of t.lanes) {
      if (!isInstrumentLane(lane)) continue;
      for (const b of lane.steps.filter(isHitBlockStep)) {
        const name = b.label ?? sourceLabel(b.source);
        const mainId = hitBlockLookId(b.id, 'main');
        blockLooks.set(mainId, hitBlockWireLook(mainId, name, b.look, blockLookLights(b.look, rig)));
        if (b.fill && !b.fill.off) {
          const fillId = hitBlockLookId(b.id, 'fill');
          blockLooks.set(fillId, hitBlockWireLook(fillId, `${name} fill`, b.fill.look, blockLookLights(b.fill.look, rig)));
        }
      }
    }
  }
  const ownLooks = new Map((t.instrumentLooks ?? []).map((l) => [l.id, cleanWireLook(l)]));
  const sentLooks = new Map<string, InstrumentLook>();
  const instrumentWire = (lane: TimelineLane): WireStep[] => {
    if (!drums) {
      for (const st of lane.steps) dropped.push({ kind: 'needs-update', label: 'Drum lights', machine: machineName(cfg), stepId: st.id });
      return [];
    }
    const out: WireStep[] = [];
    const noLights = new Set<string>();
    for (const r of playedSteps(t, lane)) {
      if (!isHitRouteStep(r)) continue;
      let lookId = r.lookId;
      if (lookId !== HIT_SILENCE_LOOK) {
        const lk = blockLooks.get(lookId) ?? ownLooks.get(lookId);
        if (!lk) {
          dropped.push({ kind: 'unresolvable', label: 'Drum lights', stepId: patternOf(r.id) ?? r.id });
          lookId = HIT_SILENCE_LOOK;
        } else if ((lk.lights ?? []).length === 0 && !lk.group) {
          noLights.add(patternOf(r.id) ?? r.id);
          lookId = HIT_SILENCE_LOOK;
        } else sentLooks.set(lookId, lk);
      }
      out.push({ id: r.id, positionBars: r.position, kind: 'hitRoute', source: { ...r.source }, lookId });
    }
    for (const stepId of noLights) dropped.push({ kind: 'no-lights', label: 'Drum lights', machine: machineName(cfg), stepId });
    // DESIGNED HITS (DEC-324; OPBridge S339 `timeline_designed_hits`): played through the routes above while OP-MidiFeed is
    // not sending that drum, silent while it is. Only to a bridge that plays them (an unknown step kind fails a push);
    // elsewhere each says it waits for that bridge's update. A role or a track only (OPBridge refuses neither).
    const designed = lane.steps.filter(isHitStep);
    if (designed.length) {
      if (!bridgePlaysDesignedHits(cfg)) {
        dropped.push({ kind: 'needs-update', label: 'Designed drum hits', machine: machineName(cfg), stepId: designed[0]!.id });
      } else {
        const total = timelineTotalBars(t);
        for (const h of designed) {
          const source = h.source.role ? { role: h.source.role } : h.source.track ? { track: h.source.track } : null;
          if (!source || !(h.position >= 0) || h.position >= total) continue;
          const v = Math.max(1, Math.min(127, Math.round(h.velocity ?? 127)));
          out.push({ id: h.id, positionBars: h.position, kind: 'hit', source, velocity: v });
        }
        out.sort((a, b) => a.positionBars - b.positionBars);
      }
    }
    return out;
  };
  const dark = timelineStartsDark(t) && bridgePlaysColour(cfg) && t.lanes.some(lightLike);
  const lightSteps = (lane: TimelineLane): TimelineStep[] =>
    dark && lane.laneKind === 'dmx-fixture' ? darkStartSteps(lane.id, playedSteps(t, lane)) : playedSteps(t, lane);
  const lanes = t.lanes.map((lane) => ({
    ...unknownFieldsOf(lane, TIMELINE_LANE_KNOWN_KEYS),
    id: lane.id,
    name: lane.name,
    // THE DMX LANE BINDING (2026-09-17). The "content half" of the envelope
    // whose "envelope half" shipped at CP-139, and NEXT.md's standing item.
    // This map built each lane as exactly `{id, name, steps}`, so `laneKind`
    // and `fixtureRef` died HERE, in our own down-converter - which is why an
    // unknown-field capture on OPBridge's side would have recovered nothing,
    // the correction we sent them 2026-09-16.
    //
    // Safe, read at their source rather than assumed: `opbridge-timelines`
    // carries no `deny_unknown_fields` anywhere and `Lane` is
    // `{id, name, steps}`, so two extra keys are IGNORED at deserialise. (The
    // same is emphatically not true of STEPS: a kind OPBridge does not model
    // fails deserialisation and 422s the whole push. That is why a step kind
    // is only sent to a bridge whose `/health.capabilities` says it plays it
    // - `colorMarker`/`lightRelease` behind `timeline_colour` (S300), see
    // `resolveStepToCues`; `animation` is still held back on purpose.)
    //
    // HONEST SCOPE: this is a wire-format commitment, not a working feature.
    // Serde discards what it does not model, so these values reach OPBridge's
    // handler and go no further until their DMX timeline engine lands. Nothing
    // is lost by that - the canonical timeline lives in the doc store, which
    // round-trips our own JSON losslessly, and this engine push is one-way.
    // What it buys is that the day they model the fields, our side is already
    // correct with no coordinated release.
    //
    // Emitted only when present, so an ordinary MIDI timeline still produces a
    // byte-identical payload and no spurious re-push.
    ...(lane.laneKind !== undefined ? { laneKind: lane.laneKind } : {}),
    ...(lane.fixtureRef !== undefined ? { fixtureRef: lane.fixtureRef } : {}),
    ...(isLightsLane(lane) && lightGroupOf(lane) !== '' ? { lightGroup: lightGroupOf(lane) } : {}),
    ...(lane.laneKind === 'dmx-rig' && lane.allLights === true && bridgePlaysAllLights(cfg) ? { allLights: true } : {}),
    // A silent LIGHTS lane says so (OPBridge S300): "if it's a light lane then
    // mute would mean off", and an empty lane alone would read as "leave the
    // light alone". Only lights lanes, so every other lane's payload is
    // byte-identical to before.
    ...(isLightsLane(lane) && !laneAudible(t, lane) ? { mute: true } : {}),
    // A muted (or un-soloed) lane goes with no steps, its id and name kept.
    steps: !laneAudible(t, lane) ? []
      // A ZONE lane to a bridge that drives whole lights only: nothing, and said (it would drive the whole light).
      : lane.fixtureRef?.subfixture !== undefined && !bridgePlaysLightZones(cfg) ? (lane.steps.forEach((st) => dropped.push({ kind: 'needs-update', label: `Zone ${lane.fixtureRef!.subfixture! + 1} of a light`, machine: machineName(cfg), stepId: st.id })), [])
      // OPVisual's show colour and palette (2026-10-28): cues to OPVisual only, never a light (`isOpvShowLane`).
      : lane.laneKind === 'opvisual-colour' ? opvColourCues(playedSteps(t, lane), cfg, dropped)
      : lane.laneKind === 'opvisual-palette' ? opvPaletteCues(playedSteps(t, lane), cfg, dropped)
      : isInstrumentLane(lane) ? instrumentWire(lane)
      : lane.laneKind === 'automation' && lane.motion ? motionCues(t, lane, cfg, dropped)
      : lane.laneKind === 'automation' ? automationCues(lane, cfg, Math.max(1, t.signature?.num ?? 4), dropped, t) : lightSteps(lane).flatMap((s): WireStep[] => {
      if (holdMasters && lane.laneKind === 'dmx-rig' && (isColorMarkerStep(s) || isLightReleaseStep(s))) {
        dropped.push({ kind: 'needs-update', label: 'Light group master', machine: machineName(cfg), stepId: s.id });
        return [];
      }
      const before = dropped.length;
      const cues = resolveStepToCues(s, cfg, t.id, dropped, Math.max(1, t.signature?.num ?? 4), compile);
      // A written-out key's warning belongs to its pattern block.
      for (let i = before; i < dropped.length; i++) dropped[i]!.stepId = patternOf(s.id) ?? s.id;
      return cues;
    }),
  }));
  // The rig's lights with no whole-light lane: one lane each, holding OFF. A light with only ZONE lanes gets one too,
  // which fans to the zones that have no lane of their own (OPBridge's rule, `zoneLaneFor`).
  if (dark) {
    const laned = new Set(t.lanes.filter((l) => l.fixtureRef && l.fixtureRef.subfixture === undefined).map((l) => l.fixtureRef!.fixtureId));
    for (const f of fixtures()) {
      if (laned.has(f.id)) continue;
      // Only the lights this computer drives (2026-10-28): each computer is sent its own part, and a dark lane naming a
      // light another computer drives either warns there ("will not play on this bridge") or, for a Hue light OPBridge
      // now drives from any PC (`timeline_hue_any_pc`), holds it off against the home's own lane. A Hue light goes dark
      // on the home; a DMX light on its owner, or on the home when it has none or its owner is off (the preview).
      if (!darkensHere(f, cfg, compile.followsHere)) continue;
      const laneId = `${t.id}~dark~${f.id}`;
      const off: TimelineStep = { id: `${laneId}~off`, kind: 'colorMarker', position: 0, color: { ...OFF }, curve: 'hold' };
      lanes.push({
        id: laneId,
        name: `${f.name} (dark start)`,
        laneKind: 'dmx-fixture',
        fixtureRef: { fixtureId: f.id },
        steps: resolveStepToCues(off, cfg, t.id, dropped, Math.max(1, t.signature?.num ?? 4), compile),
      } as (typeof lanes)[number]);
    }
  }
  // A section that loops (or stops) on a bridge that would play straight through it.
  if (t.tier === 'song' && !bridgePlaysSectionLoop(cfg)) {
    for (const s of t.sections) {
      if (s.onEnd === 'loop' || s.onEnd === 'stop') dropped.push({ kind: 'needs-update', label: `Section ${s.name} (${s.onEnd === 'loop' ? 'loops' : 'stops'})`, machine: machineName(cfg) });
    }
  }
  const totalSteps = lanes.reduce((n, l) => n + l.steps.length, 0);
  const blends = compile.sectionArrival;
  const droppedWarning = summarizeDropped(dropped);
  const stepWarnings = describeDroppedPerStep(dropped);
  // Nothing to play is "nothing fireable" (no push, the Launch toast says why)
  // UNLESS the silence is the operator's own mute or solo: then the silent
  // copy must still go, or muting the last lane of a PLAYING timeline would
  // leave OPBridge playing the old copy (found 2026-10-06 by the live-mute
  // smoke). OPBridge accepts lanes with no steps (`Lane::validate`).
  const silencedByOperator = t.lanes.some((l) => l.steps.length > 0 && !laneAudible(t, l));
  // A timeline that brings its song (OPBridge S318) has something to play even with no steps: the song.
  const bringsSong = !!t.testTrack && bridgeBringsSong(cfg) && songAudible(t);
  if (totalSteps === 0 && !silencedByOperator && !bringsSong) return { wire: null, droppedWarning, stepWarnings };
  return {
    wire: {
      ...unknownFieldsOf(t, TIMELINE_KNOWN_KEYS),
      id: t.id,
      name: t.name,
      tier: t.tier,
      // When this copy was last edited, kept by OPBridge with the rest (opbridge-timelines `Extra`), so a launch can
      // tell whether OPBridge holds a NEWER copy before pushing over it (`newerCopyOnBridge`).
      modified: t.modified,
      // The two fields both apps agreed for timelines either can edit (2026-09-23): where it was made (immutable) and
      // when it was last edited in ms, beside the ISO `modified` OPBridge already compares.
      origin: timelineOrigin(t),
      ...(Number.isFinite(Date.parse(t.modified)) ? { modifiedAt: Date.parse(t.modified) } : {}),
      // With a lead-in, an unnamed first section of that length (`timelinePlaySections`): today's engine plays it as any
      // section, unmarked, so the song carries on through it.
      sections: timelinePlaySections(t).map((s) => ({
        ...unknownFieldsOf(s, TIMELINE_SECTION_KNOWN_KEYS),
        id: s.id,
        name: s.name,
        lengthBars: Math.max(1, s.lengthBars | 0),
        // A section's end rule, only to a bridge that plays it (see bridgePlaysSectionLoop);
        // `auto-advance` is the default on both sides, so it is never sent.
        ...(s.onEnd === 'loop' || s.onEnd === 'stop') && bridgePlaysSectionLoop(cfg) ? { onEnd: s.onEnd } : {},
        // The lights blend in from the press of a jump to it (S351), unless it opens with a slam (`sectionSlam`).
        ...sectionArrivalWire(t, s.id, s.arrival, blends),
        // OP-Motion glides in from the press of a jump to this section, arriving where it starts (S340 `atEnter`).
        ...(() => {
          const at = bridgeSendsAtPress(cfg) ? sectionStartBars(t).get(s.id) : undefined;
          const enter = at === undefined ? [] : motionAtPress(t, cfg, at);
          return enter.length > 0 ? { atEnter: enter } : {};
        })(),
      })),
      lanes,
      loops: timelineLoops(t),
      // The timeline's tempo (2026-10-28, OPBridge S342): with Live gone it is the clock's (operator: "opbridge needs to
      // start playing the timeline and is controlled by the timeline"), and a re-push with a new one retimes it in place.
      // Left out when the timeline has none (a lighting timeline: OPBridge's 95). An older OPBridge ignores it.
      ...(typeof t.bpm === 'number' && Number.isFinite(t.bpm) && t.bpm > 0 ? { bpm: t.bpm } : {}),
      // When the run gives a touched fader back (S324); OPBridge keeps it verbatim and an absent one means the default.
      ...(t.touchMode ? { touchMode: t.touchMode } : {}),
      // ... and how the lights arrive when it is STARTED (S351), the same slam rule on its first section.
      ...timelineArrivalWire(t, blends),
      // What the lights the run drove do when it ends (S331): always explicit, as OPBridge's absent means `restore` and
      // ours means the tier's default. Only for a timeline with lights, and only to a bridge that honours it.
      ...(bridgeEndsLightsDark(cfg) && t.lanes.some(lightLike) ? { lightsAtEnd: timelineEndsDark(t) ? 'off' : 'restore' } : {}),
      // OP-Motion glides off when the run ends however it ends (S339 `atStop`), only to a bridge that fires it.
      // ... and an OPVisual colour lane hands every clip back (2026-10-28).
      ...(bridgeFiresAtStop(cfg) && atStopOf(t, cfg).length > 0 ? { atStop: atStopOf(t, cfg) } : {}),
      // ... and glides in from the press of a start, arriving on the bar line it lands on (S340 `atStart`).
      ...(bridgeSendsAtPress(cfg) && atStartOf(t, cfg).length > 0 ? { atStart: atStartOf(t, cfg) } : {}),
      // The drum lights looks a sent route names (S331), and the show's strobe opt-in with them (DEC-262).
      ...(sentLooks.size > 0 ? { instrumentLooks: [...sentLooks.values()] } : {}),
      ...(sentLooks.size > 0 && t.strobes ? { strobes: true } : {}),
      // The song the timeline brings (OPBridge S318): only to a bridge that plays it with the timeline.
      ...(t.testTrack && bridgeBringsSong(cfg) && songAudible(t) ? { testTrack: {
        id: t.testTrack.id,
        ...(t.testTrack.pc ? { pc: t.testTrack.pc } : {}),
        ...(t.testTrack.output !== undefined ? { output: t.testTrack.output } : {}),
        // Clips (trim and split): only the three numbers, and only to a bridge that plays them.
        // On the song's grid as it is NOW (a tempo pick or bar 1 changed since they were written keeps their music).
        ...(t.testTrack.clips && bridgeSongClips(cfg) ? { clips: wireClips(t.testTrack) } : {}),
        ...(t.testTrack.skipIntro && bridgeSkipsIntro(cfg) ? { skipIntro: true } : {}),
      } } : {}),
    },
    droppedWarning,
    stepWarnings,
  };
  });
}

/** One sentence per step that will not play, from what the compile recorded.
 *  The per-step twin of {@link summarizeDropped}, worded for a tooltip on the
 *  step itself rather than for a count in a toast. */
function describeDroppedPerStep(dropped: readonly DroppedStep[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const d of dropped) {
    if (!d.stepId) continue;
    let s: string;
    switch (d.kind) {
      case 'unsupported': s = `${d.label} can't play from a timeline yet`; break;
      case 'elsewhere': s = `plays on ${d.machine}, but this timeline plays on ${d.here}`; break;
      case 'unpaired': s = 'drives a device on a computer this one is not paired with'; break;
      case 'no-port': s = `no MIDI output is set on ${d.machine}`; break;
      case 'missing-port': s = `MIDI port "${d.port}" isn't on ${d.machine}, so it won't sound`; break;
      case 'missing-item': s = 'points at a control that no longer exists'; break;
      case 'needs-update': s = `${d.label} plays once OPBridge on ${d.machine} is updated`; break;
      case 'no-lights': s = `no lights to light on ${d.machine}: pick some in its panel`; break;
      case 'unresolvable': s = "couldn't be resolved to anything to send"; break;
    }
    const prev = out.get(d.stepId);
    out.set(d.stepId, prev ? `${prev}; ${s}` : s);
  }
  return out;
}

/**
 * Why each step of a timeline will not play where it plays, keyed by step id
 * (plan 3.3: the editor marks these on the step before any launch). Compiled
 * for the timeline's HOME machine with that machine's last known port list,
 * the same compile a push makes, so the mark and the push warning cannot
 * disagree.
 *
 * With no bridge paired at all, only what no machine could play is named (the
 * step kinds the timeline engine has no runtime for); the rest needs a
 * machine to judge against, and nothing can be launched then anyway. A Genre
 * timeline returns nothing here: the whole timeline cannot play, which the
 * editor says once rather than on every step.
 */
export function timelineStepWarnings(t: Timeline): ReadonlyMap<string, string> {
  return inProjectOf(t, () => {
  if (t.tier !== 'action' && t.tier !== 'song') return new Map();
  const cfg = resolveBridges(timelineHomeTarget(t), { ignorePanicLatch: true })[0];
  if (cfg) return toBridgeTimeline(t, cfg, { followsHere: true, knownPorts: knownPortsFor(cfg.id) }).stepWarnings;
  const out = new Map<string, string>();
  for (const lane of t.lanes) {
    for (const step of lane.steps) {
      if (isCueStep(step) || isButtonRefStep(step)) continue;
      const label = unplayableKindLabel(step);
      if (label) out.set(step.id, `${label} can't play from a timeline yet`);
    }
  }
  return out;
  });
}

// ============================================================
// Operator-facing feedback (2026-08-17)
// ============================================================
//
// A Launch that silently does nothing is the exact bug an operator hit on
// TESTSONG: every PresetChange step's pedal binding failed to resolve, so
// `toBridgeTimeline` returned `null`, `pushTimeline` never called `PUT` at
// all, and the only signal was a `console.warn` nobody was looking at.
// Callers (`TimelineLauncher.tsx`, `TimelineEditor.tsx`) MUST surface this
// via `pushToast`, not just log it — see `summarizeTimelineDispatch`.

export interface TimelineDispatchSummary {
  kind: 'ok' | 'warn' | 'error';
  message: string;
}

/** Shared push-failure summarizer used by both {@link summarizeTimelineDispatch}
 *  (Launch — a push is always followed by fire/start) and
 *  {@link summarizeTimelinePush} (the standalone "save to OPBridge" button
 *  — a push with nothing after it). `null` = the push itself succeeded;
 *  the caller decides the success message, since "launched" and "saved"
 *  read differently. `noBridgeSuffix` customizes the no-bridge-connected
 *  wording per caller ("nothing sent" vs "nothing saved"). */
function summarizePush(
  timelineName: string,
  pushResults: FireResult[],
  noBridgeSuffix: string,
): TimelineDispatchSummary | null {
  const pushFail = pushResults.find((r) => r.kind !== 'ok');
  if (!pushFail) return null;
  if (pushFail.kind === 'unsupported-step') {
    return {
      kind: 'error',
      message: `"${timelineName}" has no fireable steps on the connected bridge${pushFail.detail ? ` - ${pushFail.detail}` : ' - check item/pedal bindings.'}`,
    };
  }
  if (pushFail.kind === 'bridge-disconnected') {
    // Name the machine (2026-09-29): a timeline now plays on its home, which
    // can be a different computer from the one this browser treats as main.
    const cfg = bridges().find((b) => b.id === pushFail.target);
    if (cfg) return { kind: 'warn', message: `"${timelineName}": ${machineName(cfg)} isn't connected - ${noBridgeSuffix}.` };
  }
  if (pushFail.kind === 'no-bridges' || pushFail.kind === 'bridge-unknown' || pushFail.kind === 'bridge-disconnected') {
    return { kind: 'warn', message: `"${timelineName}": no bridge connected - ${noBridgeSuffix}.` };
  }
  return { kind: 'error', message: `"${timelineName}" did not reach OPBridge: ${plainFireReason(pushFail)}.` };
}

/** A push can succeed (every target got a 200) while still silently
 *  dropping some steps the server can't fire yet (Stream Deck, currently —
 *  see `resolveStepToCues`). Returns the first such warning across all
 *  targets, or `null` when nothing was dropped. 2026-08-21, closes the
 *  CP-100 "0 of 4 authored steps reach OPBridge, total silence" gap for the
 *  MIXED case (some steps fire fine, Stream Deck ones don't) — the
 *  all-Stream-Deck case already goes through the `unsupported-step` branch
 *  above via `toBridgeTimeline`'s improved detail message. */
function findPartialDropWarning(pushResults: FireResult[]): string | null {
  return pushResults.find((r) => r.partialDropWarning)?.partialDropWarning ?? null;
}

/** Turn one Launch attempt's push + fire results into a single operator-
 *  facing toast. Call with the FULL fan-out result arrays (every target
 *  bridge), not just the first. `null` fireResults = push failed badly
 *  enough that fire/start was never attempted. */
export function summarizeTimelineDispatch(
  timelineName: string,
  pushResults: FireResult[],
  fireResults: FireResult[] | null,
): TimelineDispatchSummary {
  const pushSummary = summarizePush(timelineName, pushResults, 'nothing sent');
  if (pushSummary) return pushSummary;
  const fireFail = fireResults?.find((r) => r.kind !== 'ok');
  if (fireFail) {
    return { kind: 'error', message: `"${timelineName}" did not start: ${plainFireReason(fireFail)}.` };
  }
  const dropWarning = findPartialDropWarning(pushResults);
  if (dropWarning) {
    return { kind: 'warn', message: `"${timelineName}" launched - ${dropWarning}` };
  }
  return { kind: 'ok', message: `"${timelineName}" launched` };
}

/** Turn a standalone "save to OPBridge" push (no fire/start after it) into
 *  an operator-facing toast — the manual Push button (2026-08-18), for
 *  confirming edits reached OPBridge without triggering playback. Same
 *  failure vocabulary as {@link summarizeTimelineDispatch}, distinct
 *  success wording. */
export function summarizeTimelinePush(
  timelineName: string,
  pushResults: FireResult[],
): TimelineDispatchSummary {
  const pushSummary = summarizePush(timelineName, pushResults, 'nothing saved');
  if (pushSummary) return pushSummary;
  if (pushResults.some((r) => r.newerCopy !== undefined)) {
    return { kind: 'warn', message: `"${timelineName}" not saved: the computer has a newer copy, which it kept. This device's copy updates when it syncs.` };
  }
  const dropWarning = findPartialDropWarning(pushResults);
  if (dropWarning) {
    return { kind: 'warn', message: `"${timelineName}" saved to OPBridge - ${dropWarning}` };
  }
  return { kind: 'ok', message: `"${timelineName}" saved to OPBridge` };
}

/** Shown when Launch is pressed with zero bridges connected at all — the
 *  real dispatch is never attempted (not even a push), so there is no
 *  `FireResult` to summarize; the local visual sim runs regardless. */
export function notLiveToastMessage(timelineName: string): TimelineDispatchSummary {
  // One sentence, said once, in the words the rest of the app uses (2026-10-16: the editor added "Playing on screen
  // only." to "playing locally only, nothing sent", and called the computer a bridge).
  return { kind: 'warn', message: `"${timelineName}" plays on screen only: no computer is connected, so nothing is sent.` };
}

// ============================================================
// Dispatch
// ============================================================

function errMsg(err: unknown): string {
  return err instanceof OpbridgeError ? `${err.kind}: ${err.message}`
    : err instanceof Error ? err.message
    : String(err);
}

async function runOnBridge(cfg: BridgeConfig, call: (client: OpbridgeHttpClient) => Promise<unknown>): Promise<FireResult> {
  const conn = getConnection(cfg.id);
  if (!conn) return { target: cfg.id, kind: 'bridge-unknown' };
  if (conn.status() !== 'connected') {
    return { target: cfg.id, kind: 'bridge-disconnected', detail: conn.status() };
  }
  try {
    await call(conn.httpClient());
    return { target: cfg.id, kind: 'ok' };
  } catch (err) {
    return { target: cfg.id, kind: 'http-error', detail: errMsg(err) };
  }
}

/** Push one timeline's current definition into OPBridge (`PUT
 *  /timelines/opbuttons/{id}`), resolving buttonRef content for each
 *  target bridge independently. Idempotent - call right before every
 *  fire/start, same "push on every tap keeps live edits in sync" contract
 *  as {@link pushSequencer}/{@link pushMacro} in dispatch.ts. */
export async function pushTimeline(
  t: Timeline,
  target = '',
  opts: PushOpts = {},
): Promise<FireResult[]> {
  const targets = resolveBridges(target);
  if (targets.length === 0) return [{ target: target || '<default>', kind: 'no-bridges' }];
  return Promise.all(targets.map((cfg) => pushTimelineOnBridge(t, cfg, opts)));
}

/** `live`: also replace the copy a bridge is PLAYING (a lane muted mid-song
 *  goes quiet at once). Sent only to a bridge that reports
 *  `timeline_live_update`; an older one gets a plain push, and its result
 *  carries no `liveCopies`, which is how a caller tells the two apart. */
type PushOpts = Pick<CompileOpts, 'followsHere' | 'siblings'> & { live?: boolean };

async function pushTimelineOnBridge(
  t: Timeline,
  cfg: BridgeConfig,
  opts: PushOpts = {},
): Promise<FireResult> {
  const conn = getConnection(cfg.id);
  if (!conn) return { target: cfg.id, kind: 'bridge-unknown' };
  if (conn.status() !== 'connected') {
    return { target: cfg.id, kind: 'bridge-disconnected', detail: conn.status() };
  }
  // 2026-08-17: `toBridgeTimeline` used to be called OUTSIDE this try —
  // a real operator hit a Launch that produced NO toast at all (not even
  // the 2026-08-17 "no fireable steps" error) on a real Song timeline with
  // real PresetChange steps, which the synthetic (fresh-item) test data in
  // every smoke never exercised. An exception thrown while resolving a
  // REAL item's fields (something the fresh-item smokes can't reproduce)
  // would reject `pushTimeline`'s `Promise.all` with nothing to catch it —
  // an unhandled rejection, invisible unless devtools happens to be open,
  // and the `.then()` chain in TimelineLauncher/TimelineEditor that would
  // have shown a toast simply never runs. Wrapping the resolver here (and
  // adding a `.catch()` at the two call sites) turns "totally silent" into
  // "a real error toast," regardless of what specifically throws.
  try {
    // Read this bridge's ports first so every cue can be checked against the
    // machine it will actually play on (2026-09-29). A failed read falls back
    // to the last list, then to no check - never to a blocked push.
    const knownPorts = await refreshKnownPorts(cfg);
    const { wire, droppedWarning } = toBridgeTimeline(t, cfg, { ...opts, knownPorts });
    if (!wire) {
      return {
        target: cfg.id,
        kind: 'unsupported-step',
        detail: t.tier !== 'action' && t.tier !== 'song'
          ? `tier "${t.tier}" has no OPBridge runtime yet`
          : droppedWarning ?? 'no step on this timeline resolves to a fireable action on this bridge',
      };
    }
    const live = opts.live === true && bridgeUpdatesLive(cfg);
    const reply = await conn.httpClient().putTimeline(CONSUMER, t.id, wire as { id: string }, { live, leaseHolder: leaseHeaderFor(t.id, cfg.id) });
    const result: FireResult = { target: cfg.id, kind: 'ok' };
    // What OPBridge itself says will not play (a light whose fixture is gone, a preset it does not have): it answered
    // these on every push and they were dropped (2026-10-16, the reliability audit). Shown with our own note.
    const bridgeSays = (reply?.warnings ?? []).filter((w) => !!w.error);
    const bridgeNote = bridgeSays.length > 0
      ? `${bridgeSays.length} change${bridgeSays.length === 1 ? '' : 's'} won't play on ${cfg.name}: ${bridgeSays[0]!.error}${bridgeSays.length > 1 ? ' (and more)' : ''}.`
      : null;
    const note = [droppedWarning, bridgeNote].filter((x): x is string => !!x).join(' ');
    if (note) result.partialDropWarning = note;
    if (live) result.liveCopies = typeof reply?.live === 'number' ? reply.live : 0;
    return result;
  } catch (err) {
    // OPBridge holds a newer copy and kept it (S324): not a failure. Nothing was overwritten; that copy plays.
    const newer = newerCopyRefusal(err);
    if (newer !== null) return { target: cfg.id, kind: 'ok', newerCopy: newer };
    // S329: another device holds this timeline's edit lease, so OPBridge kept its copy: we are locked out now.
    if (err instanceof OpbridgeError && err.httpStatus === 409 && /lease_held/.test(err.message)) {
      const name = /"holderName"\s*:\s*"([^"]*)"/.exec(err.message)?.[1] ?? null;
      noteLeaseRefused(t.id, name);
      return { target: cfg.id, kind: 'http-error', detail: `${name ?? 'Another device'} is editing this timeline, so OPBridge kept its copy` };
    }
    return { target: cfg.id, kind: 'http-error', detail: errMsg(err) };
  }
}

/** The warning a push to `target` WOULD carry, from a local compile with the
 *  cached port list - for a Launch that skips the push because OPBridge
 *  already has this exact content, so its toast still names what won't play
 *  instead of reading as a clean "launched". `null` when nothing is dropped. */
export function timelineDropWarning(
  t: Timeline,
  target: string,
  opts: Pick<CompileOpts, 'followsHere' | 'siblings'> = {},
): string | null {
  const cfg = resolveBridges(target, { ignorePanicLatch: true })[0];
  if (!cfg) return null;
  return toBridgeTimeline(t, cfg, { ...opts, knownPorts: knownPortsFor(cfg.id) }).droppedWarning;
}

/** `DELETE /timelines/opbuttons/{id}` on one bridge. Used when a timeline's
 *  home moves, so the old machine is not left holding a stale copy that a
 *  named-action route or an old launcher could still start (2026-09-29, three
 *  such copies found on the Gaming-PC, aimed at ports it no longer has). */
export async function deleteTimelineOnBridge(timelineId: string, target: string): Promise<FireResult[]> {
  const targets = resolveBridges(target, { ignorePanicLatch: true });
  if (targets.length === 0) return [{ target: target || '<default>', kind: 'no-bridges' }];
  return Promise.all(targets.map((cfg) => deleteOnBridge(cfg, timelineId)));
}

/** Delete a timeline from EVERY paired bridge - the editor's Delete. Before
 *  2026-09-29 a deleted timeline stayed on OPBridge forever (`deleteTimeline`
 *  had no caller). A bridge that is offline reports `bridge-disconnected`,
 *  which the caller names to the operator; nothing is retried behind their back. */
export async function deleteTimelineEverywhere(timelineId: string): Promise<FireResult[]> {
  const all = bridges();
  if (all.length === 0) return [];
  return Promise.all(all.map((cfg) => deleteOnBridge(cfg, timelineId)));
}

async function deleteOnBridge(cfg: BridgeConfig, timelineId: string): Promise<FireResult> {
  const conn = getConnection(cfg.id);
  if (!conn) return { target: cfg.id, kind: 'bridge-unknown' };
  if (conn.status() !== 'connected') return { target: cfg.id, kind: 'bridge-disconnected', detail: conn.status() };
  try {
    await conn.httpClient().deleteTimeline(CONSUMER, timelineId);
    return { target: cfg.id, kind: 'ok' };
  } catch (err) {
    // 404 is OPBridge's "timeline id not in library": already gone, which is
    // exactly what a delete wants (read at handlers/timelines.rs, 2026-09-29).
    if (err instanceof OpbridgeError && err.httpStatus === 404) return { target: cfg.id, kind: 'ok' };
    return { target: cfg.id, kind: 'http-error', detail: errMsg(err) };
  }
}

/** `POST /timelines/opbuttons/fire` - start a fresh Action-tier instance.
 *  The timeline must already be pushed under the same bearer ({@link pushTimeline}).
 *  `quantize` (a Launcher's Instant / Next bar) goes only to a bridge that
 *  reports `timeline_quantize`; left out, OPBridge fires at once. */
export async function fireTimelineAction(timelineId: string, target = '', quantize?: TimelineQuantize): Promise<FireResult[]> {
  const targets = resolveBridges(target);
  if (targets.length === 0) return [{ target: target || '<default>', kind: 'no-bridges' }];
  return Promise.all(targets.map((cfg) => runOnBridge(cfg, (c) =>
    c.fireTimeline(CONSUMER, timelineId, quantize && bridgeQuantizes(cfg) ? quantize : undefined))));
}

/** OPBridge's copy of `t` on `target` when it is NEWER than this device's (edited later), else null (also when it
 *  cannot say). A launch from a device holding an older copy (an iPad whose sync had not caught up with an edit made on
 *  the desktop) pushed that copy over the newer one and played the old show (2026-10-16, the reliability audit). Asked
 *  only when a launch is about to push a copy this tab has not pushed; a second of clock difference between devices
 *  is allowed. */
export async function newerCopyOnBridge(t: Timeline, target: string): Promise<{ modified: string } | null> {
  const cfg = resolveBridges(target)[0];
  const conn = cfg ? getConnection(cfg.id) : null;
  if (!conn || conn.status() !== 'connected') return null;
  // OPBridge refuses the older copy itself (S324): the push's own answer says so (`FireResult.newerCopy`).
  if (bridgeGuardsNewer(cfg!)) return null;
  try {
    const lib = await conn.httpClient().getTimelineLibrary(CONSUMER);
    const theirs = lib.timelines?.find((x) => x.id === t.id)?.modified;
    if (!theirs) return null;
    const a = Date.parse(theirs); const b = Date.parse(t.modified);
    return Number.isFinite(a) && Number.isFinite(b) && a > b + 1000 ? { modified: theirs } : null;
  } catch {
    return null;
  }
}

/** Said when a launch plays OPBridge's newer copy rather than pushing this device's older one. */
export function newerCopyMessage(t: Timeline, modified: string): string {
  const d = new Date(modified);
  const at = Number.isFinite(d.getTime()) ? ` (edited at ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})` : '';
  return `"${t.name}": the computer has a newer copy${at}, so that one plays. This device's copy is older; it updates when this device syncs.`;
}

/** `POST /timelines/opbuttons/start` - (re)start the Song-tier timeline
 *  from bar 0. Same push-first contract as {@link fireTimelineAction}, and
 *  the same `quantize` rule; left out, OPBridge waits for the next bar. `from` is `'editor'` only for the timeline
 *  editor's Play (OPBridge S351: it takes a lost clock); a pad, a cue or Navigate never sends it. */
export async function startTimelineAction(timelineId: string, target = '', quantize?: TimelineQuantize, fromBar?: number, from?: 'editor'): Promise<FireResult[]> {
  const targets = resolveBridges(target);
  if (targets.length === 0) return [{ target: target || '<default>', kind: 'no-bridges' }];
  return Promise.all(targets.map(async (cfg) => {
    // The answer says whether the timeline's song came with it (OPBridge S318), and whether the timeline now leads the
    // clock (S342); both kept for the launch toast.
    let song: FireResult['song'];
    let leads: FireResult['leads'];
    // Play now only to a bridge that has it (S340); an older one gets its default, the next bar line.
    const q = quantize === 'playNow' ? (bridgePlaysNow(cfg) ? quantize : undefined) : quantize;
    const r = await runOnBridge(cfg, async (c) => {
      const a = await c.startTimeline(
        CONSUMER, timelineId, q && bridgeQuantizes(cfg) ? q : undefined,
        fromBar !== undefined && fromBar > 0 && bridgeStartsAtBar(cfg) ? fromBar : undefined,
        from,
      );
      if (a && typeof a === 'object' && a.testTrack) {
        const tt = a.testTrack;
        song = {
          playing: tt.playing === true,
          ...(tt.output !== undefined ? { output: tt.output } : {}),
          ...(tt.reason !== undefined ? { reason: tt.reason } : {}),
          ...(tt.message !== undefined ? { message: tt.message } : {}),
        };
      }
      if (a && typeof a === 'object' && typeof a.timelineLeads?.bpm === 'number') leads = { bpm: a.timelineLeads.bpm };
    });
    return { ...r, ...(song ? { song } : {}), ...(leads ? { leads } : {}) };
  }));
}

/** `POST /timelines/opbuttons/stop` - stop the active Song timeline. */
export async function stopTimelineAction(target = ''): Promise<FireResult[]> {
  const targets = resolveBridges(target);
  if (targets.length === 0) return [{ target: target || '<default>', kind: 'no-bridges' }];
  return Promise.all(targets.map((cfg) => runOnBridge(cfg, (c) => c.stopTimeline(CONSUMER))));
}

/** `POST /timelines/opbuttons/pause`. */
export async function pauseTimelineAction(target = ''): Promise<FireResult[]> {
  const targets = resolveBridges(target);
  if (targets.length === 0) return [{ target: target || '<default>', kind: 'no-bridges' }];
  return Promise.all(targets.map((cfg) => runOnBridge(cfg, (c) => c.pauseTimeline(CONSUMER))));
}

/** `POST /timelines/opbuttons/resume`. */
export async function resumeTimelineAction(target = ''): Promise<FireResult[]> {
  const targets = resolveBridges(target);
  if (targets.length === 0) return [{ target: target || '<default>', kind: 'no-bridges' }];
  return Promise.all(targets.map((cfg) => runOnBridge(cfg, (c) => c.resumeTimeline(CONSUMER))));
}

/** `POST /timelines/opbuttons/jumpToSection` - quantized to next bar.
 *  Only meaningful against an already-active Song; the caller should
 *  {@link startTimelineAction} first when nothing is active yet. */
export async function jumpToSectionAction(sectionId: string, target = '', id?: string): Promise<FireResult[]> {
  const targets = resolveBridges(target);
  if (targets.length === 0) return [{ target: target || '<default>', kind: 'no-bridges' }];
  return Promise.all(targets.map((cfg) => runOnBridge(cfg, (c) => c.jumpToSectionTimeline(CONSUMER, sectionId, id))));
}

/** Whether `cfg` takes the ONE-call section jump (`jumpToSection {sectionId, id}`: jump if `id` is the active Song, else
 *  start it at that section on the next bar line). OPBridge S339, `timeline_jump_starts`, released v1.15.222. */
export function bridgeJumpStarts(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['timeline_jump_starts'] === true;
}

/** `GET /timelines/opbuttons/state` on ONE resolved bridge (first target
 *  only - state is a single-bridge read, not a fan-out). `null` on any
 *  failure (no bridge, disconnected, network, 401 during a migration
 *  window) - never throws, matching every other read in this codebase. */
export async function readTimelineState(target = ''): Promise<TimelineStateSnapshot | null> {
  const cfg = resolveBridges(target)[0];
  if (!cfg) return null;
  const conn = getConnection(cfg.id);
  if (!conn || conn.status() !== 'connected') return null;
  try {
    return await conn.httpClient().getTimelineState(CONSUMER);
  } catch {
    return null;
  }
}
